/**
 * Elite Batch 12 Part 5 — Workflow Templates 1128–1147
 * Preference management, price alerts, retargeting, product video, launch campaigns,
 * push notifications, QR opt-in, quiz capture, RSS automation, re-engagement,
 * and real-time personalization workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function bufferNode(
  id: string,
  label: string,
  y: number,
  text: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.bufferapp.com/1/updates/create.json",
        headers: [
          { key: "Authorization", value: "Bearer {buffer_token}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: [
          { key: "text", value: text },
          { key: "profile_ids[]", value: "{buffer_profile_id}" },
          { key: "scheduled_at", value: "{scheduled_time}" },
        ],
        variables: [
          { name: "success", customName: "buffer_success", type: "boolean" },
          {
            name: "updates[0].id",
            customName: "buffer_update_id",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}
function sendgridNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  htmlContent: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.sendgrid.com/v3/mail/send",
        headers: [
          { key: "Authorization", value: "Bearer {sendgrid_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "personalizations", value: `[{"to":[{"email":"${to}"}]}]` },
          {
            key: "from",
            value: '{"email":"{from_email}","name":"{company_name}"}',
          },
          { key: "subject", value: subject },
          {
            key: "content",
            value: `[{"type":"text/html","value":"${htmlContent}"}]`,
          },
        ],
        variables: [
          { name: "statusCode", customName: "sg_status", type: "number" },
        ],
      },
    },
    x,
  );
}
function shopifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{shopify_store}.myshopify.com/admin/api/2024-01/${endpoint}`,
        headers: [
          { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 12 PART 5 — TEMPLATES 1128–1147
// =============================================================================

export const ELITE_BATCH12_PART5: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1128. PREFERENCE CENTER MANAGER (8 nodes)
  // ===================================================================

  "Preference Center Manager": {
    summary:
      "Listens for preference update webhooks, extracts and parses preference data via runCode, validates consent against regulations, syncs preferences to HubSpot CRM, updates email platform settings in SendGrid, adjusts SMS preferences in Twilio, sends a confirmation notification email, and logs all preference changes to Google Sheets for audit.",
    nodes: [
      trigger("Webhook: Preference update received", "webhook_app", {
        source: "preference_center",
        event: "preference.updated",
        howItStarts:
          "Fires when a subscriber submits an updated preference form from the preference center, capturing their email, communication channel choices, frequency settings, and content category selections",
        setup:
          "Configure webhook endpoint from your preference center form. Set HubSpot API key with contacts write access. Provide SendGrid API key for email preference sync. Configure Twilio Account SID and Auth Token for SMS preference management. Set Google Sheets spreadsheet ID for audit logging.",
        inputFields: [],
      }),

      // 2. Preference parser (runCode)
      n("2", "runCode", "Parse and normalize preference data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse raw webhook payload and normalize preference selections
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = (payload.email || payload.subscriber_email || payload.contact_email || "").toLowerCase().trim();
const prefs = payload.preferences || payload.selections || payload.choices || {};
const emailOptIn = prefs.email === true || prefs.email === "yes" || prefs.email_marketing === true;
const smsOptIn = prefs.sms === true || prefs.sms === "yes" || prefs.sms_marketing === true;
const frequency = prefs.frequency || prefs.email_frequency || "weekly";
const categories = Array.isArray(prefs.categories) ? prefs.categories : (prefs.topics || []);
const timestamp = new Date().toISOString();
return {
  subscriber_email: email,
  email_opt_in: emailOptIn ? "yes" : "no",
  sms_opt_in: smsOptIn ? "yes" : "no",
  preferred_frequency: frequency,
  content_categories: JSON.stringify(categories),
  categories_count: categories.length,
  update_timestamp: timestamp,
  has_valid_email: email.includes("@") ? "yes" : "no"
};`,
          outputVariables: [
            { name: "subscriber_email", type: "string" },
            { name: "email_opt_in", type: "string" },
            { name: "sms_opt_in", type: "string" },
            { name: "preferred_frequency", type: "string" },
            { name: "content_categories", type: "string" },
            { name: "categories_count", type: "number" },
            { name: "update_timestamp", type: "string" },
            { name: "has_valid_email", type: "string" },
          ],
        },
      }),

      // 3. Consent validator (condition)
      n("3", "condition", "Validate consent: valid email present", 520, {
        conditionDetails: {
          conditions: [
            { variable: "{has_valid_email}", operator: "equals", value: "yes" },
          ],
        },
      }),

      // 4. CRM preference updater (HubSpot)
      hubspotPostNode(
        "4",
        "Update CRM preferences in HubSpot",
        760,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{subscriber_email}"}]}]',
          },
          {
            key: "properties",
            value: '["email","firstname","lastname","hs_email_optout"]',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
          { name: "total", customName: "contacts_found", type: "number" },
        ],
      ),

      // 5. Email platform sync (SendGrid)
      n("5", "hitApi", "Sync email preferences to SendGrid", 1000, {
        hitApiDetails: {
          method: "PUT",
          url: "https://api.sendgrid.com/v3/marketing/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "contacts",
              value:
                '[{"email":"{subscriber_email}","custom_fields":{"frequency":"{preferred_frequency}","categories":"{content_categories}","email_opt_in":"{email_opt_in}"}}]',
            },
          ],
          variables: [
            { name: "job_id", customName: "sg_sync_job_id", type: "string" },
          ],
        },
      }),

      // 6. SMS preference handler (Twilio)
      n("6", "hitApi", "Update SMS preferences via Twilio", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://messaging.twilio.com/v1/Services/{twilio_messaging_service_sid}/Compliance/Usa2p",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth_base64}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "PhoneNumber", value: "{subscriber_phone}" },
            { key: "OptInStatus", value: "{sms_opt_in}" },
          ],
          variables: [
            { name: "sid", customName: "twilio_update_sid", type: "string" },
          ],
        },
      }),

      // 7. Confirmation notification (email)
      emailNode(
        "7",
        "Send preference update confirmation",
        1480,
        "{subscriber_email}",
        "Your preferences have been updated",
        "Hi there,\\n\\nWe have updated your communication preferences:\\n\\n- Email: {email_opt_in}\\n- SMS: {sms_opt_in}\\n- Frequency: {preferred_frequency}\\n- Content categories: {content_categories}\\n\\nYou can change these at any time from your preference center.\\n\\nBest regards,\\n{company_name}",
      ),

      // 8. Preference analytics logger (Sheets)
      sheetsNode(
        "8",
        "Log preference changes to Google Sheets",
        1720,
        "PreferenceAudit",
        '[["{update_timestamp}","{subscriber_email}","{email_opt_in}","{sms_opt_in}","{preferred_frequency}","{content_categories}","{categories_count}","{hubspot_contact_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1129. PREFERENCE REFRESH BEFORE SUNSET (7 nodes)
  // ===================================================================

  "Preference Refresh Before Sunset": {
    summary:
      "Scans for sunset candidates on a weekly schedule, generates a last-chance re-engagement email via AI, dispatches it, monitors for responses after a waiting period, updates re-engaged subscribers in HubSpot, executes sunset suppression for non-responders via runCode, and logs all sunset outcomes to Google Sheets.",
    nodes: [
      trigger("Schedule: Sunset candidate scan", "schedule", {
        schedule: "0 7 * * 1 (Monday at 7 AM)",
        howItStarts:
          "A weekly schedule identifies subscribers approaching the sunset threshold (no opens or clicks in 75+ days), giving them one final chance to re-engage before being suppressed",
        setup:
          "Configure email platform API key to identify unengaged subscribers (75+ days). Set OpenAI API key for AI-generated last-chance emails. Provide HubSpot API key for CRM updates. Set Google Sheets spreadsheet ID for sunset audit logging.",
        inputFields: [],
      }),

      // 2. AI last-chance email
      aiNode(
        "2",
        "AI: Generate last-chance re-engagement email",
        280,
        "You are a retention email specialist. Write a heartfelt, urgent last-chance email that makes subscribers feel valued while clearly communicating they will be removed if they do not engage. Use empathy, not guilt. Return JSON.",
        "Write a last-chance email for subscribers who have not engaged in 75+ days. Company: {company_name}. This is their FINAL email before being sunset. Return subject_line (urgency without spam triggers), preview_text (40 chars), email_body_html (warm tone, clear CTA to confirm subscription, mention what they will miss), preference_center_cta (text for preference center link).",
        [
          { field: "subject_line", as: "lastchance_subject", type: "string" },
          { field: "preview_text", as: "lastchance_preview", type: "string" },
          { field: "email_body_html", as: "lastchance_body", type: "string" },
          {
            field: "preference_center_cta",
            as: "pref_center_cta",
            type: "string",
          },
        ],
      ),

      // 3. Preference refresh email dispatch
      sendgridNode(
        "3",
        "Dispatch last-chance preference refresh email",
        520,
        "{subscriber_email}",
        "{lastchance_subject}",
        "{lastchance_body}",
      ),

      // 4. Response monitor (delay + condition)
      n("4", "delayAction", "Wait 10 days for subscriber response", 760, {
        delayDetails: { duration: 10, unit: "days" },
      }),

      // 4b. Condition: did they re-engage?
      n("4b", "condition", "Check if subscriber re-engaged", 920, {
        conditionDetails: {
          conditions: [
            { variable: "{sg_status}", operator: "equals", value: "202" },
          ],
        },
      }),

      // 5. Re-engaged subscriber handler (HubSpot) — True branch
      hubspotPostNode(
        "5",
        "Mark subscriber as re-engaged in HubSpot",
        1100,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{subscriber_email}"}]}]',
          },
          {
            key: "properties",
            value: '["email","hs_lead_status","sunset_status"]',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
          { name: "total", customName: "reengaged_found", type: "number" },
        ],
        250,
      ),

      // 6. Sunset executor (runCode) — False branch
      n(
        "6",
        "runCode",
        "Execute sunset suppression for non-responders",
        1100,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Process sunset suppression for subscribers who did not respond
const email = vars.subscriber_email || "";
const suppressionPayload = {
  recipient_emails: [email],
  reason: "preference_refresh_no_response",
  sunset_date: new Date().toISOString(),
  days_inactive: 85,
  last_chance_sent: true,
  last_chance_responded: false
};
// Add to SendGrid suppression group
const sgResponse = await fetch('https://api.sendgrid.com/v3/asm/groups/{suppression_group_id}/suppressions', {
  method: 'POST',
  headers: { 'Authorization': 'Bearer ' + vars.sendgrid_key, 'Content-Type': 'application/json' },
  body: JSON.stringify({ recipient_emails: [email] })
});
const sgResult = await sgResponse.json();
return {
  sunset_email: email,
  suppression_result: JSON.stringify(sgResult),
  sunset_action: "suppressed",
  sunset_timestamp: new Date().toISOString(),
  sunset_reason: "no_response_to_last_chance"
};`,
            outputVariables: [
              { name: "sunset_email", type: "string" },
              { name: "suppression_result", type: "string" },
              { name: "sunset_action", type: "string" },
              { name: "sunset_timestamp", type: "string" },
              { name: "sunset_reason", type: "string" },
            ],
          },
        },
        500,
      ),

      // 7. Sunset analytics logger (Sheets)
      sheetsNode(
        "7",
        "Log sunset outcomes to Google Sheets",
        1340,
        "SunsetAudit",
        '[["{sunset_timestamp}","{subscriber_email}","{sunset_action}","{sunset_reason}","{sg_status}","{reengaged_found}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "4b"),
      e("4b", "5", "output_1"),
      e("4b", "6", "output_2"),
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1130. PREFERENCE SURVEY CAPTURE AFTER SIGNUP (7 nodes)
  // ===================================================================

  "Preference Survey Capture After Signup": {
    summary:
      "Triggers on new user signup, uses AI to generate personalized survey questions, dispatches the survey via email, collects responses via webhook, builds a preference profile in code, enriches the CRM contact in HubSpot, and follows up with non-responders after a delay.",
    nodes: [
      trigger("Webhook: New user signup", "webhook_app", {
        source: "signup_form",
        event: "user.created",
        howItStarts:
          "Fires when a new user completes signup, initiating a preference survey flow to capture content interests, communication preferences, and demographic data for personalized experiences",
        setup:
          "Configure webhook from your signup system. Set OpenAI API key for AI-generated survey questions. Provide SendGrid or email service credentials. Configure HubSpot API key for CRM enrichment. Set webhook endpoint for survey response collection.",
        inputFields: [],
      }),

      // 2. AI survey question generator
      aiNode(
        "2",
        "AI: Generate personalized survey questions",
        280,
        "You are a UX researcher specializing in onboarding surveys. Generate a concise preference survey (5-7 questions) that feels conversational, not like a form. Questions should capture content interests, communication channel preferences, and frequency expectations. Return JSON.",
        "Generate a preference survey for a new subscriber at {company_name}. Industry: {company_industry}. Return survey_subject (email subject), survey_intro (friendly 2-sentence intro), questions (array of objects with question_text, type: radio/checkbox/text, options: array of choices), survey_html (complete HTML email body with embedded survey links), estimated_completion_time (e.g. 2 minutes).",
        [
          { field: "survey_subject", as: "survey_subject", type: "string" },
          { field: "survey_intro", as: "survey_intro", type: "string" },
          { field: "questions", as: "survey_questions_json", type: "string" },
          { field: "survey_html", as: "survey_body_html", type: "string" },
          {
            field: "estimated_completion_time",
            as: "survey_time",
            type: "string",
          },
        ],
      ),

      // 3. Survey email dispatch
      sendgridNode(
        "3",
        "Dispatch preference survey email",
        520,
        "{subscriber_email}",
        "{survey_subject}",
        "{survey_body_html}",
      ),

      // 4. Survey response collector (runCode — processes webhook callback)
      n("4", "runCode", "Collect and validate survey responses", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Collect survey responses from callback webhook
const responses = JSON.parse(vars.survey_responses || "{}");
const email = vars.subscriber_email || "";
const answered = Object.keys(responses).length;
const totalQuestions = JSON.parse(vars.survey_questions_json || "[]").length;
const completionRate = totalQuestions > 0 ? ((answered / totalQuestions) * 100).toFixed(0) : "0";
const interests = responses.content_interests || responses.topics || [];
const channelPref = responses.channel_preference || "email";
const frequencyPref = responses.frequency || "weekly";
return {
  respondent_email: email,
  questions_answered: answered,
  completion_rate_pct: completionRate,
  content_interests_json: JSON.stringify(interests),
  channel_preference: channelPref,
  frequency_preference: frequencyPref,
  survey_completed: answered > 0 ? "yes" : "no",
  response_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "respondent_email", type: "string" },
            { name: "questions_answered", type: "number" },
            { name: "completion_rate_pct", type: "string" },
            { name: "content_interests_json", type: "string" },
            { name: "channel_preference", type: "string" },
            { name: "frequency_preference", type: "string" },
            { name: "survey_completed", type: "string" },
            { name: "response_timestamp", type: "string" },
          ],
        },
      }),

      // 5. Preference profile builder (runCode)
      n("5", "runCode", "Build subscriber preference profile", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build a unified preference profile from survey responses
const interests = JSON.parse(vars.content_interests_json || "[]");
const channel = vars.channel_preference || "email";
const frequency = vars.frequency_preference || "weekly";
const profile = {
  email: vars.respondent_email,
  interests: interests,
  primary_channel: channel,
  frequency: frequency,
  profile_completeness: vars.completion_rate_pct + "%",
  created_at: vars.response_timestamp,
  segment_tags: interests.slice(0, 3).map(i => "interest_" + i.toLowerCase().replace(/\\s+/g, "_")),
  engagement_score: Math.min(100, parseInt(vars.completion_rate_pct) + 20)
};
return {
  preference_profile_json: JSON.stringify(profile),
  segment_tags_csv: profile.segment_tags.join(","),
  profile_completeness: profile.profile_completeness,
  engagement_score: profile.engagement_score,
  primary_interest: interests.length > 0 ? interests[0] : "general"
};`,
          outputVariables: [
            { name: "preference_profile_json", type: "string" },
            { name: "segment_tags_csv", type: "string" },
            { name: "profile_completeness", type: "string" },
            { name: "engagement_score", type: "number" },
            { name: "primary_interest", type: "string" },
          ],
        },
      }),

      // 6. CRM profile enrichment (HubSpot)
      hubspotPostNode(
        "6",
        "Enrich CRM contact with preference profile",
        1240,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{respondent_email}"}]}]',
          },
          {
            key: "properties",
            value: '["email","firstname","preference_profile","segment_tags"]',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
          { name: "total", customName: "contacts_enriched", type: "number" },
        ],
      ),

      // 7. Non-responder follow-up (delay)
      n(
        "7",
        "delayAction",
        "Wait 3 days then follow up with non-responders",
        1480,
        {
          delayDetails: { duration: 3, unit: "days" },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1131. PREFERENCE-CENTER INVITE (6 nodes)
  // ===================================================================

  "Preference-Center Invite": {
    summary:
      "Selects a subscriber segment for preference center invitations, uses AI to craft a personalized invitation email, dispatches it via SendGrid, tracks engagement in Google Sheets, updates CRM records in HubSpot when subscribers engage, and logs overall campaign results.",
    nodes: [
      trigger("Schedule: Preference center invitation cycle", "schedule", {
        schedule: "0 9 * * 3 (Wednesday at 9 AM)",
        howItStarts:
          "A weekly schedule selects subscribers who have not visited the preference center in 60+ days, sending them a personalized invitation to update their communication preferences",
        setup:
          "Configure subscriber data source API. Set OpenAI API key for AI-generated invitation copy. Provide SendGrid API key for email dispatch. Set Google Sheets spreadsheet ID for engagement tracking. Configure HubSpot API key for CRM updates.",
        inputFields: [],
      }),

      // 2. AI invitation writer
      aiNode(
        "2",
        "AI: Write preference center invitation",
        280,
        "You are an email engagement specialist. Write a warm, benefit-focused invitation to the preference center. Emphasize subscriber control over their experience. Keep it concise and action-oriented. Return JSON.",
        "Write a preference center invitation email for {company_name}. The subscriber has not updated preferences in 60+ days. Return subject_line (curiosity-driven, not spammy), email_body_html (benefits of updating: better content, right frequency, relevant topics; single CTA button to preference center), cta_text (button text), preview_text (40 chars max).",
        [
          { field: "subject_line", as: "invite_subject", type: "string" },
          { field: "email_body_html", as: "invite_body_html", type: "string" },
          { field: "cta_text", as: "invite_cta_text", type: "string" },
          { field: "preview_text", as: "invite_preview", type: "string" },
        ],
      ),

      // 3. Preference center invitation email
      sendgridNode(
        "3",
        "Send preference center invitation email",
        520,
        "{subscriber_email}",
        "{invite_subject}",
        "{invite_body_html}",
      ),

      // 4. Engagement tracker (Sheets)
      sheetsNode(
        "4",
        "Track invitation engagement in Sheets",
        760,
        "PrefCenterInvites",
        '[["{subscriber_email}","{invite_subject}","{sg_status}","{invite_cta_text}","sent","' +
          new Date().toISOString() +
          '"]]',
      ),

      // 5. CRM update on engagement (HubSpot)
      hubspotPostNode(
        "5",
        "Update CRM on preference center engagement",
        1000,
        "/crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{subscriber_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","preference_center_last_invite","lifecycle_stage"]',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
          { name: "total", customName: "contacts_updated", type: "number" },
        ],
      ),

      // 6. Campaign results logger
      sheetsNode(
        "6",
        "Log campaign results summary",
        1240,
        "CampaignResults",
        '[["{invite_subject}","{contacts_updated}","{sg_status}","preference_center_invite","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 1132. PRICE-DROP ALERT OPT-IN (8 nodes)
  // ===================================================================

  "Price-Drop Alert Opt-In": {
    summary:
      "Triggers on price watch signup, monitors product prices via Shopify, detects price drops with runCode logic, generates AI-powered alert messaging, sends email and SMS alerts via SendGrid and Twilio, tracks conversions, and logs alert performance to Google Sheets.",
    nodes: [
      trigger("Webhook: Price watch signup", "webhook_app", {
        source: "storefront",
        event: "price_watch.created",
        howItStarts:
          "Fires when a customer opts in to price drop alerts for a specific product, capturing their contact details and the product they are watching",
        setup:
          "Configure webhook from your storefront price watch widget. Set Shopify API key for product price monitoring. Set OpenAI API key for AI-generated alert messages. Provide SendGrid and Twilio credentials for email/SMS alerts. Set Google Sheets spreadsheet ID for performance logging.",
        inputFields: [],
      }),

      // 2. Product price monitor (Shopify)
      shopifyNode(
        "2",
        "Fetch current product price from Shopify",
        280,
        "GET",
        "products/{watched_product_id}.json",
        [],
        [
          {
            name: "product.title",
            customName: "product_title",
            type: "string",
          },
          {
            name: "product.variants[0].price",
            customName: "current_price",
            type: "string",
          },
          {
            name: "product.variants[0].compare_at_price",
            customName: "original_price",
            type: "string",
          },
          {
            name: "product.image.src",
            customName: "product_image_url",
            type: "string",
          },
          {
            name: "product.handle",
            customName: "product_handle",
            type: "string",
          },
        ],
      ),

      // 3. Price drop detector (runCode)
      n("3", "runCode", "Detect price drop and calculate savings", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare current price against watched price to detect a drop
const currentPrice = parseFloat(vars.current_price) || 0;
const originalPrice = parseFloat(vars.original_price) || currentPrice;
const watchedPrice = parseFloat(vars.watched_price) || originalPrice;
const hasDrop = currentPrice < watchedPrice;
const savingsAmount = hasDrop ? (watchedPrice - currentPrice).toFixed(2) : "0.00";
const savingsPercent = hasDrop && watchedPrice > 0 ? ((1 - currentPrice / watchedPrice) * 100).toFixed(0) : "0";
const productUrl = "https://" + vars.shopify_store + ".myshopify.com/products/" + vars.product_handle;
return {
  price_dropped: hasDrop ? "yes" : "no",
  savings_amount: savingsAmount,
  savings_percent: savingsPercent,
  current_price_display: "$" + currentPrice.toFixed(2),
  original_price_display: "$" + watchedPrice.toFixed(2),
  product_url: productUrl,
  detection_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "price_dropped", type: "string" },
            { name: "savings_amount", type: "string" },
            { name: "savings_percent", type: "string" },
            { name: "current_price_display", type: "string" },
            { name: "original_price_display", type: "string" },
            { name: "product_url", type: "string" },
            { name: "detection_timestamp", type: "string" },
          ],
        },
      }),

      // 4. AI alert message
      aiNode(
        "4",
        "AI: Generate price drop alert message",
        760,
        "You are a conversion copywriter specializing in urgency-driven e-commerce alerts. Write a compelling price drop notification that creates urgency without being pushy. Include the exact savings. Return JSON.",
        "Product: {product_title}. Was: {original_price_display}. Now: {current_price_display}. Savings: {savings_percent}% ({savings_amount}). Return email_subject (urgency + savings), email_body_html (hero image placeholder, price comparison, CTA to buy now), sms_message (under 160 chars, includes savings and short link placeholder), push_headline (under 50 chars).",
        [
          { field: "email_subject", as: "alert_email_subject", type: "string" },
          { field: "email_body_html", as: "alert_email_body", type: "string" },
          { field: "sms_message", as: "alert_sms_message", type: "string" },
          { field: "push_headline", as: "alert_push_headline", type: "string" },
        ],
      ),

      // 5. Price drop email alert
      sendgridNode(
        "5",
        "Send price drop email alert via SendGrid",
        1000,
        "{subscriber_email}",
        "{alert_email_subject}",
        "{alert_email_body}",
      ),

      // 6. SMS instant alert (Twilio)
      smsNode(
        "6",
        "Send price drop SMS alert via Twilio",
        1240,
        "{subscriber_phone}",
        "{alert_sms_message}",
      ),

      // 7. Conversion tracker (runCode)
      n("7", "runCode", "Track alert conversion metrics", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate alert delivery metrics for tracking
const emailSent = vars.sg_status === "202" || vars.sg_status === 202;
const smsSent = vars.sms_sid ? true : false;
return {
  alert_id: "alert_" + Date.now(),
  email_delivered: emailSent ? "yes" : "no",
  sms_delivered: smsSent ? "yes" : "no",
  channels_used: (emailSent ? 1 : 0) + (smsSent ? 1 : 0),
  product_watched: vars.product_title || "",
  savings_offered: vars.savings_percent + "%",
  alert_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "alert_id", type: "string" },
            { name: "email_delivered", type: "string" },
            { name: "sms_delivered", type: "string" },
            { name: "channels_used", type: "number" },
            { name: "product_watched", type: "string" },
            { name: "savings_offered", type: "string" },
            { name: "alert_timestamp", type: "string" },
          ],
        },
      }),

      // 8. Alert performance logger (Sheets)
      sheetsNode(
        "8",
        "Log alert performance to Google Sheets",
        1720,
        "PriceDropAlerts",
        '[["{alert_timestamp}","{subscriber_email}","{product_watched}","{current_price_display}","{original_price_display}","{savings_offered}","{email_delivered}","{sms_delivered}","{channels_used}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1133. PRICING PAGE VISITOR RETARGETING (7 nodes)
  // ===================================================================

  "Pricing Page Visitor Retargeting": {
    summary:
      "Triggers on pricing page visits, scores visitor intent via runCode, enriches known visitors from HubSpot CRM, syncs high-intent visitors to Meta retargeting audiences, generates AI-powered retargeting ad copy, builds retargeting campaigns, and alerts sales reps via Slack for high-intent prospects.",
    nodes: [
      trigger("Webhook: Pricing page visit detected", "webhook_app", {
        source: "website_analytics",
        event: "page.viewed",
        howItStarts:
          "Fires when a visitor views the pricing page, capturing their session data, visitor ID, referral source, and any known contact information for retargeting",
        setup:
          "Configure website analytics webhook for pricing page views. Set HubSpot API key for contact enrichment. Provide Meta Marketing API access token. Set OpenAI API key for ad copy generation. Configure Slack webhook for sales alerts.",
        inputFields: [],
      }),

      // 2. Visitor intent scorer (runCode)
      n("2", "runCode", "Score visitor intent from session data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score pricing page visitor intent based on behavior signals
const payload = JSON.parse(vars.webhook_payload || "{}");
const visitorId = payload.visitor_id || payload.anonymous_id || "unknown";
const email = (payload.email || payload.identified_email || "").toLowerCase().trim();
const timeOnPage = parseInt(payload.time_on_page_seconds) || 0;
const scrollDepth = parseInt(payload.scroll_depth_pct) || 0;
const pageViews = parseInt(payload.session_page_views) || 1;
const referrer = payload.referrer || "direct";
const pricingClicks = parseInt(payload.cta_clicks) || 0;
// Intent scoring algorithm
let score = 0;
score += timeOnPage > 120 ? 30 : timeOnPage > 60 ? 20 : timeOnPage > 30 ? 10 : 5;
score += scrollDepth > 80 ? 25 : scrollDepth > 50 ? 15 : 5;
score += pageViews > 5 ? 20 : pageViews > 3 ? 10 : 5;
score += pricingClicks > 0 ? 15 : 0;
score += referrer.includes("google") ? 5 : referrer.includes("linkedin") ? 10 : 0;
const intentLevel = score >= 70 ? "high" : score >= 40 ? "medium" : "low";
const isKnown = email.includes("@") ? "yes" : "no";
return {
  visitor_id: visitorId,
  visitor_email: email,
  intent_score: score,
  intent_level: intentLevel,
  is_known_visitor: isKnown,
  time_on_pricing: timeOnPage,
  scroll_depth: scrollDepth,
  referral_source: referrer,
  scoring_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "visitor_id", type: "string" },
            { name: "visitor_email", type: "string" },
            { name: "intent_score", type: "number" },
            { name: "intent_level", type: "string" },
            { name: "is_known_visitor", type: "string" },
            { name: "time_on_pricing", type: "number" },
            { name: "scroll_depth", type: "number" },
            { name: "referral_source", type: "string" },
            { name: "scoring_timestamp", type: "string" },
          ],
        },
      }),

      // 3. Known visitor CRM enrichment (HubSpot)
      hubspotGetNode(
        "3",
        "Enrich known visitor from HubSpot CRM",
        520,
        'crm/v3/objects/contacts/search?filterGroups=[{"filters":[{"propertyName":"email","operator":"EQ","value":"{visitor_email}"}]}]&properties=email,firstname,lastname,company,jobtitle,lifecyclestage,total_revenue',
        [
          {
            name: "results[0].properties.company",
            customName: "visitor_company",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "visitor_title",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          { name: "total", customName: "crm_match_count", type: "number" },
        ],
      ),

      // 4. Meta retargeting audience sync
      n("4", "hitApi", "Sync visitor to Meta retargeting audience", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{meta_custom_audience_id}/users",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "payload",
              value: '{"schema":["EMAIL"],"data":[["{visitor_email}"]]}',
            },
          ],
          variables: [
            {
              name: "audience_id",
              customName: "meta_audience_id",
              type: "string",
            },
            {
              name: "num_received",
              customName: "meta_users_synced",
              type: "number",
            },
          ],
        },
      }),

      // 5. AI retargeting ad generator
      aiNode(
        "5",
        "AI: Generate retargeting ad copy",
        1000,
        "You are a performance marketing copywriter specializing in retargeting. Write ad copy that reminds pricing page visitors about the value proposition without being creepy. Focus on benefits and social proof. Return JSON.",
        "Generate retargeting ad copy for a visitor who spent {time_on_pricing}s on the pricing page. Intent level: {intent_level}. Company: {company_name}. Visitor company: {visitor_company}. Return ad_headline (40 chars max), ad_primary_text (125 chars), ad_description (30 chars), ad_cta (e.g. Learn More, Start Free Trial), landing_page_variant (pricing/demo/trial based on intent).",
        [
          { field: "ad_headline", as: "retarget_headline", type: "string" },
          {
            field: "ad_primary_text",
            as: "retarget_primary_text",
            type: "string",
          },
          {
            field: "ad_description",
            as: "retarget_description",
            type: "string",
          },
          { field: "ad_cta", as: "retarget_cta", type: "string" },
          {
            field: "landing_page_variant",
            as: "landing_variant",
            type: "string",
          },
        ],
      ),

      // 6. Retargeting campaign builder
      n("6", "hitApi", "Create Meta retargeting campaign", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/act_{meta_ad_account_id}/ads",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "name",
              value:
                "Retarget Pricing - {intent_level} Intent - {scoring_timestamp}",
            },
            { key: "adset_id", value: "{meta_retarget_adset_id}" },
            {
              key: "creative",
              value:
                '{"title":"{retarget_headline}","body":"{retarget_primary_text}","link_description":"{retarget_description}","call_to_action_type":"{retarget_cta}"}',
            },
            { key: "status", value: "ACTIVE" },
          ],
          variables: [{ name: "id", customName: "meta_ad_id", type: "string" }],
        },
      }),

      // 7. Sales alert for high-intent (Slack)
      slackNode(
        "7",
        "Alert sales team on high-intent pricing visitor",
        1480,
        "🎯 *High-Intent Pricing Visitor*\\nEmail: {visitor_email}\\nCompany: {visitor_company} | Title: {visitor_title}\\nIntent Score: {intent_score} ({intent_level})\\nTime on Pricing: {time_on_pricing}s | Scroll: {scroll_depth}%\\nSource: {referral_source}\\nRetargeting: Ad {meta_ad_id} created\\nAction: Review and reach out if qualified",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1134. PRODUCT DEMO VIDEO AD (8 nodes)
  // ===================================================================

  "Product Demo Video Ad": {
    summary:
      "Starts from a demo brief trigger, generates an AI demo script, routes through an approval gate for script review, renders the video, adapts format for different platforms via runCode, publishes to TikTok, distributes across platforms via Buffer, and tracks demo ad performance in Google Sheets.",
    nodes: [
      trigger("Webhook: Demo video brief submitted", "webhook_app", {
        source: "creative_brief_tool",
        event: "brief.submitted",
        howItStarts:
          "Fires when a marketer submits a product demo video brief including product features, target audience, demo flow, duration preference, and platform targets",
        setup:
          "Configure webhook from your creative brief tool. Set OpenAI API key for AI script generation. Provide Creatomate API key for video rendering. Set TikTok Marketing API credentials. Configure Buffer API for cross-platform distribution. Set Google Sheets spreadsheet ID for performance tracking.",
        inputFields: [],
      }),

      // 2. AI demo script generator
      aiNode(
        "2",
        "AI: Generate product demo video script",
        280,
        "You are a product demo video scriptwriter. Create engaging, clear demo scripts that showcase product value in the first 3 seconds. Include visual directions, voiceover text, and on-screen text. Structure for attention retention. Return JSON.",
        "Create a product demo video script. Product: {product_name}. Key features: {demo_features}. Target audience: {target_audience}. Duration: {video_duration}s. Return script_title, hook_line (first 3 seconds), scene_breakdown (array of scenes with timestamp, visual_direction, voiceover, on_screen_text), cta_text, background_music_mood, total_scenes (number), estimated_duration_seconds.",
        [
          { field: "script_title", as: "demo_script_title", type: "string" },
          { field: "hook_line", as: "demo_hook", type: "string" },
          { field: "scene_breakdown", as: "demo_scenes_json", type: "string" },
          { field: "cta_text", as: "demo_cta", type: "string" },
          {
            field: "background_music_mood",
            as: "demo_music_mood",
            type: "string",
          },
          { field: "total_scenes", as: "demo_scene_count", type: "number" },
          {
            field: "estimated_duration_seconds",
            as: "demo_duration",
            type: "number",
          },
        ],
      ),

      // 3. Script review approval gate
      n("3", "approvalGate", "Script review approval gate", 520, {
        approvalGateDetails: {
          approvers: ["{creative_lead_email}"],
          message:
            "Please review the demo video script: '{demo_script_title}'\\n\\nHook: {demo_hook}\\nScenes: {demo_scene_count}\\nDuration: {demo_duration}s\\nCTA: {demo_cta}\\nMusic mood: {demo_music_mood}\\n\\nApprove to proceed with video rendering.",
          timeoutHours: 48,
        },
      }),

      // 4. Video rendering (Creatomate)
      n("4", "hitApi", "Render demo video via Creatomate", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.creatomate.com/v1/renders",
          headers: [
            { key: "Authorization", value: "Bearer {creatomate_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "template_id", value: "{demo_video_template_id}" },
            {
              key: "modifications",
              value:
                '{"hook_text":"{demo_hook}","scenes":"{demo_scenes_json}","cta":"{demo_cta}","music_mood":"{demo_music_mood}"}',
            },
          ],
          variables: [
            { name: "[0].id", customName: "render_id", type: "string" },
            {
              name: "[0].url",
              customName: "rendered_video_url",
              type: "string",
            },
            { name: "[0].status", customName: "render_status", type: "string" },
          ],
        },
      }),

      // 5. Platform format adapter (runCode)
      n("5", "runCode", "Adapt video format for target platforms", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Prepare platform-specific video specifications
const videoUrl = vars.rendered_video_url || "";
const platforms = {
  tiktok: { width: 1080, height: 1920, format: "mp4", aspect: "9:16", maxDuration: 60 },
  instagram_reels: { width: 1080, height: 1920, format: "mp4", aspect: "9:16", maxDuration: 90 },
  youtube_shorts: { width: 1080, height: 1920, format: "mp4", aspect: "9:16", maxDuration: 60 },
  facebook_feed: { width: 1080, height: 1080, format: "mp4", aspect: "1:1", maxDuration: 120 },
  linkedin_feed: { width: 1920, height: 1080, format: "mp4", aspect: "16:9", maxDuration: 120 }
};
const specs = Object.entries(platforms).map(([name, spec]) => ({
  platform: name,
  ...spec,
  source_url: videoUrl
}));
return {
  platform_specs_json: JSON.stringify(specs),
  platforms_count: specs.length,
  primary_video_url: videoUrl,
  tiktok_ready: "yes",
  cross_platform_formats: Object.keys(platforms).join(", ")
};`,
          outputVariables: [
            { name: "platform_specs_json", type: "string" },
            { name: "platforms_count", type: "number" },
            { name: "primary_video_url", type: "string" },
            { name: "tiktok_ready", type: "string" },
            { name: "cross_platform_formats", type: "string" },
          ],
        },
      }),

      // 6. TikTok ad publisher
      n("6", "hitApi", "Publish demo ad to TikTok", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://business-api.tiktok.com/open_api/v1.3/ad/create/",
          headers: [
            { key: "Access-Token", value: "{tiktok_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "advertiser_id", value: "{tiktok_advertiser_id}" },
            { key: "adgroup_id", value: "{tiktok_adgroup_id}" },
            { key: "ad_name", value: "Demo: {demo_script_title}" },
            { key: "ad_text", value: "{demo_hook} {demo_cta}" },
            { key: "video_id", value: "{tiktok_video_id}" },
            { key: "call_to_action", value: "LEARN_MORE" },
          ],
          variables: [
            { name: "data.ad_id", customName: "tiktok_ad_id", type: "string" },
            {
              name: "code",
              customName: "tiktok_response_code",
              type: "number",
            },
          ],
        },
      }),

      // 7. Cross-platform distribution (Buffer)
      bufferNode(
        "7",
        "Distribute demo video across platforms via Buffer",
        1480,
        "🎬 {demo_script_title}\\n\\n{demo_hook}\\n\\n{demo_cta}\\n\\n📹 Watch the full demo: {primary_video_url}",
      ),

      // 8. Demo performance tracker (Sheets)
      sheetsNode(
        "8",
        "Track demo ad performance in Sheets",
        1720,
        "DemoVideoAds",
        '[["{demo_script_title}","{demo_scene_count}","{demo_duration}","{render_status}","{tiktok_ad_id}","{buffer_update_id}","{cross_platform_formats}","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1135. PRODUCT DETAIL PAGE VIDEO (8 nodes)
  // ===================================================================

  "Product Detail Page Video": {
    summary:
      "Triggers on a PDP video brief, generates an AI script optimized for product detail pages, validates script quality via condition, renders the video, optimizes format in runCode, uploads to Shopify, sets up A/B testing, and tracks performance in Google Sheets.",
    nodes: [
      trigger("Webhook: PDP video brief submitted", "webhook_app", {
        source: "ecommerce_team",
        event: "pdp_video.requested",
        howItStarts:
          "Fires when the e-commerce team requests a product detail page video, providing product details, key selling points, target customer persona, and desired video style",
        setup:
          "Configure webhook from your e-commerce content management tool. Set OpenAI API key for AI script generation. Provide Creatomate API key for video rendering. Configure Shopify API key for PDP video upload. Set Google Sheets spreadsheet ID for performance tracking.",
        inputFields: [],
      }),

      // 2. AI PDP video script
      aiNode(
        "2",
        "AI: Generate PDP video script",
        280,
        "You are a product video specialist for e-commerce. Create scripts optimized for product detail pages: show the product in use, highlight key benefits, address common objections, and drive add-to-cart. Videos should be 15-30 seconds. Return JSON.",
        "Create a PDP video script. Product: {product_name}. Key benefits: {product_benefits}. Price: {product_price}. Target persona: {target_persona}. Return script_title, opening_shot (product reveal), benefit_scenes (array with visual and text for each benefit), social_proof_moment (review quote or stat), closing_cta, recommended_duration_seconds, quality_score (1-10 self-assessment).",
        [
          { field: "script_title", as: "pdp_script_title", type: "string" },
          { field: "opening_shot", as: "pdp_opening", type: "string" },
          { field: "benefit_scenes", as: "pdp_scenes_json", type: "string" },
          {
            field: "social_proof_moment",
            as: "pdp_social_proof",
            type: "string",
          },
          { field: "closing_cta", as: "pdp_cta", type: "string" },
          {
            field: "recommended_duration_seconds",
            as: "pdp_duration",
            type: "number",
          },
          {
            field: "quality_score",
            as: "script_quality_score",
            type: "number",
          },
        ],
      ),

      // 3. Script quality review condition
      n("3", "condition", "Check script quality score meets threshold", 520, {
        conditionDetails: {
          conditions: [
            {
              variable: "{script_quality_score}",
              operator: "greater_than",
              value: "6",
            },
          ],
        },
      }),

      // 4. Video rendering (Creatomate)
      n("4", "hitApi", "Render PDP video via Creatomate", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.creatomate.com/v1/renders",
          headers: [
            { key: "Authorization", value: "Bearer {creatomate_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "template_id", value: "{pdp_video_template_id}" },
            {
              key: "modifications",
              value:
                '{"opening":"{pdp_opening}","scenes":"{pdp_scenes_json}","social_proof":"{pdp_social_proof}","cta":"{pdp_cta}"}',
            },
          ],
          variables: [
            { name: "[0].id", customName: "pdp_render_id", type: "string" },
            { name: "[0].url", customName: "pdp_video_url", type: "string" },
            {
              name: "[0].status",
              customName: "pdp_render_status",
              type: "string",
            },
          ],
        },
      }),

      // 5. Video format optimizer (runCode)
      n("5", "runCode", "Optimize video format for PDP display", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Optimize video metadata and format for PDP embedding
const videoUrl = vars.pdp_video_url || "";
const duration = parseInt(vars.pdp_duration) || 20;
const productHandle = vars.product_handle || "product";
const optimizedMeta = {
  url: videoUrl,
  alt_text: vars.pdp_script_title + " - " + vars.product_name,
  poster_timestamp: Math.min(3, Math.floor(duration / 4)),
  autoplay: true,
  muted: true,
  loop: duration <= 30,
  preload: "metadata",
  width: 1080,
  height: 1080,
  aspect_ratio: "1:1",
  format: "mp4",
  file_size_estimate_mb: (duration * 0.8).toFixed(1)
};
return {
  optimized_video_url: videoUrl,
  video_alt_text: optimizedMeta.alt_text,
  video_metadata_json: JSON.stringify(optimizedMeta),
  should_loop: optimizedMeta.loop ? "yes" : "no",
  poster_time: optimizedMeta.poster_timestamp + "s",
  estimated_file_size: optimizedMeta.file_size_estimate_mb + "MB"
};`,
          outputVariables: [
            { name: "optimized_video_url", type: "string" },
            { name: "video_alt_text", type: "string" },
            { name: "video_metadata_json", type: "string" },
            { name: "should_loop", type: "string" },
            { name: "poster_time", type: "string" },
            { name: "estimated_file_size", type: "string" },
          ],
        },
      }),

      // 6. PDP video upload (Shopify)
      shopifyNode(
        "6",
        "Upload video to Shopify PDP",
        1240,
        "POST",
        "products/{shopify_product_id}/media.json",
        [
          {
            key: "media",
            value:
              '[{"media_content_type":"VIDEO","original_source":"{optimized_video_url}","alt":"{video_alt_text}"}]',
          },
        ],
        [
          {
            name: "media[0].id",
            customName: "shopify_media_id",
            type: "string",
          },
          {
            name: "media[0].status",
            customName: "shopify_upload_status",
            type: "string",
          },
        ],
      ),

      // 7. A/B test configuration (abTest)
      n("7", "abTest", "A/B test PDP with and without video", 1480, {
        abTestDetails: {
          variants: [
            {
              name: "Control",
              weight: 50,
              description: "PDP without video — standard product images only",
            },
            {
              name: "Video",
              weight: 50,
              description: "PDP with embedded demo video — autoplay muted",
            },
          ],
          metric: "add_to_cart_rate",
          duration_days: 14,
        },
      }),

      // 8. Performance tracker (Sheets)
      sheetsNode(
        "8",
        "Track PDP video performance in Sheets",
        1720,
        "PDPVideoPerformance",
        '[["{pdp_script_title}","{product_name}","{pdp_duration}","{pdp_render_status}","{shopify_media_id}","{estimated_file_size}","{script_quality_score}","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1136. PRODUCT LAUNCH EARLY ACCESS (8 nodes)
  // ===================================================================

  "Product Launch Early Access": {
    summary:
      "Triggers on early access campaign launch, builds a VIP segment from HubSpot, generates AI invitation content, dispatches early access emails and VIP SMS alerts via Twilio, tracks landing page engagement, monitors stock and demand via Shopify, and logs campaign performance to Google Sheets.",
    nodes: [
      trigger("Manual: Launch early access campaign", "manual", {
        howItStarts:
          "A marketer manually triggers the early access campaign for an upcoming product launch, specifying the product, launch date, VIP segment criteria, and access window duration",
        setup:
          "Configure HubSpot API key for VIP segment building. Set OpenAI API key for AI invitation content. Provide SendGrid and Twilio credentials for multi-channel delivery. Configure Shopify API for stock monitoring. Set Google Sheets spreadsheet ID for campaign logging.",
        inputFields: [
          {
            name: "product_name",
            type: "string",
            example: "Premium Widget Pro",
          },
          { name: "launch_date", type: "string", example: "2026-04-15" },
          { name: "early_access_hours", type: "number", example: "48" },
          {
            name: "vip_segment_criteria",
            type: "string",
            example: "total_revenue > 500 AND lifecycle = customer",
          },
          { name: "product_price", type: "string", example: "$149.99" },
          { name: "shopify_product_id", type: "string", example: "8234567890" },
        ],
      }),

      // 2. VIP segment builder (HubSpot)
      hubspotPostNode(
        "2",
        "Build VIP early access segment in HubSpot",
        280,
        "/crm/v3/lists",
        [
          {
            key: "name",
            value: "Early Access VIP - {product_name} - {launch_date}",
          },
          { key: "objectTypeId", value: "0-1" },
          { key: "processingType", value: "DYNAMIC" },
          {
            key: "filterBranches",
            value:
              '[{"filterBranchType":"OR","filters":[{"propertyName":"total_revenue","operator":"GT","value":"500"},{"propertyName":"lifecyclestage","operator":"EQ","value":"customer"}]}]',
          },
        ],
        [
          { name: "listId", customName: "vip_list_id", type: "string" },
          { name: "name", customName: "vip_list_name", type: "string" },
        ],
      ),

      // 3. AI early access invitation
      aiNode(
        "3",
        "AI: Generate early access invitation content",
        520,
        "You are a product launch copywriter specializing in VIP exclusivity. Create content that makes recipients feel special and privileged. Build anticipation without overselling. Return JSON.",
        "Create early access invitation content. Product: {product_name}. Price: {product_price}. Launch date: {launch_date}. Early access window: {early_access_hours} hours. Return email_subject (exclusivity-focused), email_body_html (VIP appreciation, product teaser, countdown timer placeholder, early access CTA), sms_message (under 160 chars, urgency + exclusivity), landing_headline (hero text for early access page), landing_subheadline.",
        [
          { field: "email_subject", as: "ea_email_subject", type: "string" },
          { field: "email_body_html", as: "ea_email_body", type: "string" },
          { field: "sms_message", as: "ea_sms_message", type: "string" },
          {
            field: "landing_headline",
            as: "ea_landing_headline",
            type: "string",
          },
          {
            field: "landing_subheadline",
            as: "ea_landing_subheadline",
            type: "string",
          },
        ],
      ),

      // 4. Early access email
      sendgridNode(
        "4",
        "Send early access invitation email",
        760,
        "{subscriber_email}",
        "{ea_email_subject}",
        "{ea_email_body}",
      ),

      // 5. SMS VIP alert (Twilio)
      smsNode(
        "5",
        "Send VIP SMS alert via Twilio",
        1000,
        "{subscriber_phone}",
        "{ea_sms_message}",
      ),

      // 6. Early access landing tracker (runCode)
      n("6", "runCode", "Track early access landing page engagement", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate early access engagement metrics
const emailStatus = vars.sg_status || "unknown";
const smsDelivered = vars.sms_sid ? true : false;
const launchDate = new Date(vars.launch_date || Date.now());
const now = new Date();
const hoursUntilLaunch = Math.max(0, Math.floor((launchDate.getTime() - now.getTime()) / 3600000));
return {
  campaign_id: "ea_" + Date.now(),
  email_dispatch_status: emailStatus,
  sms_dispatch_status: smsDelivered ? "delivered" : "not_sent",
  channels_used: (emailStatus === "202" ? 1 : 0) + (smsDelivered ? 1 : 0),
  hours_until_launch: hoursUntilLaunch,
  early_access_starts: new Date(launchDate.getTime() - (parseInt(vars.early_access_hours) || 48) * 3600000).toISOString(),
  tracking_timestamp: now.toISOString()
};`,
          outputVariables: [
            { name: "campaign_id", type: "string" },
            { name: "email_dispatch_status", type: "string" },
            { name: "sms_dispatch_status", type: "string" },
            { name: "channels_used", type: "number" },
            { name: "hours_until_launch", type: "number" },
            { name: "early_access_starts", type: "string" },
            { name: "tracking_timestamp", type: "string" },
          ],
        },
      }),

      // 7. Stock + demand monitor (Shopify)
      shopifyNode(
        "7",
        "Monitor stock and demand via Shopify",
        1480,
        "GET",
        "products/{shopify_product_id}.json",
        [],
        [
          {
            name: "product.variants[0].inventory_quantity",
            customName: "stock_available",
            type: "number",
          },
          {
            name: "product.variants[0].sku",
            customName: "product_sku",
            type: "string",
          },
          {
            name: "product.status",
            customName: "product_status",
            type: "string",
          },
        ],
      ),

      // 8. Campaign logger (Sheets)
      sheetsNode(
        "8",
        "Log early access campaign to Sheets",
        1720,
        "EarlyAccessCampaigns",
        '[["{tracking_timestamp}","{product_name}","{product_price}","{launch_date}","{early_access_hours}h","{vip_list_id}","{channels_used}","{stock_available}","{campaign_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1137. PRODUCT LAUNCH WAITLIST NURTURE (9 nodes)
  // ===================================================================

  "Product Launch Waitlist Nurture": {
    summary:
      "Triggers on waitlist signup, creates a CRM entry in HubSpot, generates AI build-up content, delivers a 3-email nurture sequence (welcome, behind-the-scenes, social proof) with delays, sends a priority launch-day email, implements a referral booster via runCode, and logs waitlist analytics to Google Sheets.",
    nodes: [
      trigger("Webhook: Waitlist signup", "webhook_app", {
        source: "waitlist_form",
        event: "waitlist.joined",
        howItStarts:
          "Fires when a prospect joins the product launch waitlist, capturing their email, name, and referral source to begin the nurture sequence",
        setup:
          "Configure webhook from your waitlist landing page. Set HubSpot API key for CRM entry creation. Provide OpenAI API key for AI content generation. Set SendGrid API key for email delivery. Configure Google Sheets spreadsheet ID for analytics.",
        inputFields: [],
      }),

      // 2. CRM entry creation (HubSpot)
      hubspotPostNode(
        "2",
        "Create waitlist contact in HubSpot",
        280,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{subscriber_email}","firstname":"{subscriber_name}","lifecyclestage":"subscriber","hs_lead_status":"WAITLIST","waitlist_product":"{product_name}","waitlist_date":"{join_timestamp}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "confirmed_email",
            type: "string",
          },
        ],
      ),

      // 3. AI build-up content
      aiNode(
        "3",
        "AI: Generate waitlist nurture content series",
        520,
        "You are a product launch content strategist. Create a 3-part email nurture series that builds anticipation and keeps waitlist subscribers engaged. Each email should reveal something new and escalate excitement. Return JSON.",
        "Create a 3-email waitlist nurture series. Product: {product_name}. Launch date: {launch_date}. Key features: {product_features}. Return email_1_subject, email_1_body_html (welcome + teaser), email_2_subject, email_2_body_html (behind the scenes), email_3_subject, email_3_body_html (social proof + countdown), launch_email_subject, launch_email_body_html (launch day priority access), referral_cta (text encouraging sharing).",
        [
          { field: "email_1_subject", as: "nurture1_subject", type: "string" },
          { field: "email_1_body_html", as: "nurture1_body", type: "string" },
          { field: "email_2_subject", as: "nurture2_subject", type: "string" },
          { field: "email_2_body_html", as: "nurture2_body", type: "string" },
          { field: "email_3_subject", as: "nurture3_subject", type: "string" },
          { field: "email_3_body_html", as: "nurture3_body", type: "string" },
          {
            field: "launch_email_subject",
            as: "launch_subject",
            type: "string",
          },
          {
            field: "launch_email_body_html",
            as: "launch_body",
            type: "string",
          },
          { field: "referral_cta", as: "referral_cta_text", type: "string" },
        ],
      ),

      // 4. Nurture email 1 — welcome + teaser
      sendgridNode(
        "4",
        "Send nurture email 1: Welcome + teaser",
        760,
        "{confirmed_email}",
        "{nurture1_subject}",
        "{nurture1_body}",
      ),

      // 5. Nurture email 2 — behind the scenes (delay)
      n(
        "5a",
        "delayAction",
        "Wait 3 days before behind-the-scenes email",
        940,
        {
          delayDetails: { duration: 3, unit: "days" },
        },
      ),

      n("5b", "sendEmail", "Send nurture email 2: Behind the scenes", 1080, {
        sendEmailDetails: {
          to: "{confirmed_email}",
          content: { subject: "{nurture2_subject}", body: "{nurture2_body}" },
        },
      }),

      // 6. Nurture email 3 — social proof (delay)
      n("6a", "delayAction", "Wait 4 days before social proof email", 1260, {
        delayDetails: { duration: 4, unit: "days" },
      }),

      n(
        "6b",
        "sendEmail",
        "Send nurture email 3: Social proof + countdown",
        1400,
        {
          sendEmailDetails: {
            to: "{confirmed_email}",
            content: { subject: "{nurture3_subject}", body: "{nurture3_body}" },
          },
        },
      ),

      // 7. Launch day priority email
      emailNode(
        "7",
        "Send launch day priority access email",
        1580,
        "{confirmed_email}",
        "{launch_subject}",
        "{launch_body}",
      ),

      // 8. Referral booster (runCode)
      n("8", "runCode", "Generate personalized referral link and track", 1820, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate unique referral link for waitlist subscriber
const email = vars.confirmed_email || "";
const contactId = vars.hubspot_contact_id || "";
const referralCode = "WL" + contactId.slice(-6).toUpperCase() + Date.now().toString(36).slice(-4).toUpperCase();
const referralLink = "https://" + (vars.company_domain || "example.com") + "/waitlist?ref=" + referralCode;
return {
  referral_code: referralCode,
  referral_link: referralLink,
  referral_email: email,
  referral_generated_at: new Date().toISOString(),
  nurture_emails_sent: 3,
  launch_email_sent: "yes",
  total_touchpoints: 4
};`,
          outputVariables: [
            { name: "referral_code", type: "string" },
            { name: "referral_link", type: "string" },
            { name: "referral_email", type: "string" },
            { name: "referral_generated_at", type: "string" },
            { name: "nurture_emails_sent", type: "number" },
            { name: "launch_email_sent", type: "string" },
            { name: "total_touchpoints", type: "number" },
          ],
        },
      }),

      // 9. Waitlist analytics (Sheets)
      sheetsNode(
        "9",
        "Log waitlist analytics to Google Sheets",
        2060,
        "WaitlistNurture",
        '[["{confirmed_email}","{hubspot_contact_id}","{product_name}","{nurture_emails_sent}","{launch_email_sent}","{referral_code}","{referral_link}","{total_touchpoints}","{referral_generated_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5a"),
      e("5a", "5b"),
      e("5b", "6a"),
      e("6a", "6b"),
      e("6b", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1138. PRODUCT UPDATE COMMUNITY POST (7 nodes)
  // ===================================================================

  "Product Update Community Post": {
    summary:
      "Triggers on product update events, categorizes the update type via runCode, generates AI community post content, publishes to Discord, distributes across social media via Buffer, checks if the update is major for email notifications, and tracks community engagement in Google Sheets.",
    nodes: [
      trigger("Webhook: Product update published", "webhook_app", {
        source: "product_management",
        event: "release.published",
        howItStarts:
          "Fires when a product update or release note is published, capturing the update title, description, category, version number, and affected features for community distribution",
        setup:
          "Configure webhook from your product management tool (e.g., Linear, Jira, Notion). Set OpenAI API key for AI post generation. Provide Discord webhook URL for community posts. Configure Buffer API for social media distribution. Set Google Sheets spreadsheet ID for engagement tracking.",
        inputFields: [],
      }),

      // 2. Update categorizer (runCode)
      n("2", "runCode", "Categorize product update type and impact", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook and categorize the product update
const payload = JSON.parse(vars.webhook_payload || "{}");
const title = payload.title || payload.release_title || payload.name || "Product Update";
const description = payload.description || payload.body || payload.release_notes || "";
const version = payload.version || payload.tag_name || "latest";
const features = payload.features || payload.changes || [];
const isMajor = version.includes(".0.0") || description.toLowerCase().includes("major") || features.length > 5;
const isBreaking = description.toLowerCase().includes("breaking") || description.toLowerCase().includes("migration");
const category = isBreaking ? "breaking_change" : isMajor ? "major_release" : features.length > 2 ? "feature_update" : "improvement";
const emoji = category === "breaking_change" ? "⚠️" : category === "major_release" ? "🚀" : category === "feature_update" ? "✨" : "🔧";
return {
  update_title: title,
  update_description: description,
  update_version: version,
  update_category: category,
  category_emoji: emoji,
  is_major_update: isMajor || isBreaking ? "yes" : "no",
  features_count: features.length,
  features_json: JSON.stringify(features),
  categorized_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "update_title", type: "string" },
            { name: "update_description", type: "string" },
            { name: "update_version", type: "string" },
            { name: "update_category", type: "string" },
            { name: "category_emoji", type: "string" },
            { name: "is_major_update", type: "string" },
            { name: "features_count", type: "number" },
            { name: "features_json", type: "string" },
            { name: "categorized_at", type: "string" },
          ],
        },
      }),

      // 3. AI community post generator
      aiNode(
        "3",
        "AI: Generate community post content",
        520,
        "You are a developer community manager. Write engaging, informative update posts that celebrate progress and explain changes clearly. Use appropriate tone for the update severity. Return JSON.",
        "Write a community post about this product update. Title: {update_title}. Version: {update_version}. Category: {update_category}. Description: {update_description}. Features count: {features_count}. Return discord_post (markdown formatted, 2000 char limit, includes changelog highlights), social_post (280 chars for Twitter/X), community_headline (catchy one-liner), update_summary (2-3 sentences for email).",
        [
          { field: "discord_post", as: "discord_content", type: "string" },
          { field: "social_post", as: "social_content", type: "string" },
          {
            field: "community_headline",
            as: "community_headline",
            type: "string",
          },
          { field: "update_summary", as: "email_summary", type: "string" },
        ],
      ),

      // 4. Discord community post
      n("4", "hitApi", "Post update to Discord community", 760, {
        hitApiDetails: {
          method: "POST",
          url: "{discord_webhook_url}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            {
              key: "content",
              value:
                "{category_emoji} **{community_headline}**\\n\\n{discord_content}",
            },
            { key: "username", value: "{company_name} Updates" },
          ],
          variables: [
            { name: "id", customName: "discord_message_id", type: "string" },
          ],
        },
      }),

      // 5. Social media distribution (Buffer)
      bufferNode(
        "5",
        "Distribute update across social media via Buffer",
        1000,
        "{category_emoji} {community_headline}\\n\\n{social_content}\\n\\n#ProductUpdate #v{update_version}",
      ),

      // 6. Email update for major features condition
      n("6", "condition", "Is this a major update requiring email?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "{is_major_update}", operator: "equals", value: "yes" },
          ],
        },
      }),

      // 6b. Email for major updates (True branch)
      emailNode(
        "6b",
        "Send major update email to subscribers",
        1420,
        "{subscriber_email}",
        "{category_emoji} {community_headline} — v{update_version}",
        "{email_summary}\\n\\nSee the full changelog: {changelog_url}",
        250,
      ),

      // 7. Community engagement tracker (Sheets)
      sheetsNode(
        "7",
        "Track community engagement in Sheets",
        1600,
        "ProductUpdates",
        '[["{categorized_at}","{update_title}","{update_version}","{update_category}","{features_count}","{discord_message_id}","{buffer_update_id}","{is_major_update}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "6b", "output_1"),
      e("6", "7", "output_2"),
      e("6b", "7"),
    ],
  },

  // ===================================================================
  // 1139. PRODUCT-PAGE SIGN-UP CTA (7 nodes)
  // ===================================================================

  "Product-Page Sign-Up CTA": {
    summary:
      "Triggers on product page signup form submission, tags product interest via runCode, creates a CRM contact in HubSpot, generates AI product-specific welcome content, sends a personalized welcome email, configures back-in-stock and price alert preferences, and logs product interest data to Google Sheets.",
    nodes: [
      trigger("Webhook: Product page signup submitted", "webhook_form", {
        source: "storefront",
        event: "product_signup.created",
        howItStarts:
          "Fires when a visitor submits a signup form on a specific product page, capturing their email, the product they are interested in, and any preferences selected on the form",
        setup:
          "Configure webhook from your storefront product page signup forms. Set HubSpot API key for contact creation. Provide OpenAI API key for AI welcome content. Set SendGrid API key for email delivery. Configure Google Sheets spreadsheet ID for interest logging.",
        inputFields: [],
      }),

      // 2. Product interest tagger (runCode)
      n("2", "runCode", "Tag product interest from signup context", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract and tag product interest from webhook payload
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = (payload.email || payload.subscriber_email || "").toLowerCase().trim();
const productId = payload.product_id || payload.item_id || "";
const productName = payload.product_name || payload.item_name || "Unknown Product";
const productCategory = payload.category || payload.product_type || "general";
const signupSource = payload.source || "product_page";
const priceAlertWanted = payload.price_alert === true || payload.price_alert === "yes";
const stockAlertWanted = payload.stock_alert === true || payload.stock_alert === "yes";
const tags = ["product_interest_" + productCategory.toLowerCase().replace(/\\s+/g, "_")];
if (priceAlertWanted) tags.push("price_alert_subscriber");
if (stockAlertWanted) tags.push("stock_alert_subscriber");
return {
  signup_email: email,
  interested_product_id: productId,
  interested_product_name: productName,
  product_category: productCategory,
  signup_source: signupSource,
  wants_price_alerts: priceAlertWanted ? "yes" : "no",
  wants_stock_alerts: stockAlertWanted ? "yes" : "no",
  interest_tags: tags.join(","),
  signup_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "signup_email", type: "string" },
            { name: "interested_product_id", type: "string" },
            { name: "interested_product_name", type: "string" },
            { name: "product_category", type: "string" },
            { name: "signup_source", type: "string" },
            { name: "wants_price_alerts", type: "string" },
            { name: "wants_stock_alerts", type: "string" },
            { name: "interest_tags", type: "string" },
            { name: "signup_timestamp", type: "string" },
          ],
        },
      }),

      // 3. CRM contact creator (HubSpot)
      hubspotPostNode(
        "3",
        "Create or update CRM contact in HubSpot",
        520,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{signup_email}","lifecyclestage":"subscriber","product_interest":"{interested_product_name}","product_category":"{product_category}","signup_source":"{signup_source}","price_alert_opt_in":"{wants_price_alerts}","stock_alert_opt_in":"{wants_stock_alerts}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          { name: "properties.email", customName: "crm_email", type: "string" },
        ],
      ),

      // 4. AI product-specific welcome
      aiNode(
        "4",
        "AI: Generate product-specific welcome content",
        760,
        "You are an e-commerce email specialist. Write a warm, product-focused welcome email that acknowledges the specific product the subscriber is interested in. Include relevant product details and next steps. Return JSON.",
        "Write a welcome email for a new subscriber interested in: {interested_product_name} (category: {product_category}). They want price alerts: {wants_price_alerts}. Stock alerts: {wants_stock_alerts}. Company: {company_name}. Return welcome_subject (product-specific), welcome_body_html (acknowledges their interest, product highlights, what to expect next, relevant recommendations), product_tip (one useful tip about the product category).",
        [
          { field: "welcome_subject", as: "welcome_subject", type: "string" },
          { field: "welcome_body_html", as: "welcome_body", type: "string" },
          { field: "product_tip", as: "product_tip", type: "string" },
        ],
      ),

      // 5. Product welcome email
      sendgridNode(
        "5",
        "Send product-specific welcome email",
        1000,
        "{signup_email}",
        "{welcome_subject}",
        "{welcome_body}",
      ),

      // 6. Back-in-stock + price alert setup (runCode)
      n(
        "6",
        "runCode",
        "Configure back-in-stock and price alert preferences",
        1240,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Set up alert subscriptions based on user preferences
const alertConfig = {
  email: vars.signup_email,
  product_id: vars.interested_product_id,
  product_name: vars.interested_product_name,
  alerts: []
};
if (vars.wants_price_alerts === "yes") {
  alertConfig.alerts.push({
    type: "price_drop",
    threshold_pct: 10,
    channels: ["email"],
    active: true
  });
}
if (vars.wants_stock_alerts === "yes") {
  alertConfig.alerts.push({
    type: "back_in_stock",
    channels: ["email"],
    active: true
  });
}
return {
  alert_config_json: JSON.stringify(alertConfig),
  alerts_configured: alertConfig.alerts.length,
  price_alert_active: vars.wants_price_alerts === "yes" ? "yes" : "no",
  stock_alert_active: vars.wants_stock_alerts === "yes" ? "yes" : "no",
  config_timestamp: new Date().toISOString()
};`,
            outputVariables: [
              { name: "alert_config_json", type: "string" },
              { name: "alerts_configured", type: "number" },
              { name: "price_alert_active", type: "string" },
              { name: "stock_alert_active", type: "string" },
              { name: "config_timestamp", type: "string" },
            ],
          },
        },
      ),

      // 7. Product interest logger (Sheets)
      sheetsNode(
        "7",
        "Log product interest data to Sheets",
        1480,
        "ProductPageSignups",
        '[["{signup_timestamp}","{signup_email}","{interested_product_name}","{product_category}","{signup_source}","{wants_price_alerts}","{wants_stock_alerts}","{alerts_configured}","{hubspot_contact_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1140. PROMOTIONAL OFFER CAMPAIGN (8 nodes)
  // ===================================================================

  "Promotional Offer Campaign": {
    summary:
      "Triggers on a promotion brief, generates AI urgency-driven copy, segments the audience via runCode, launches email campaigns via SendGrid, sends SMS flash alerts via Twilio, creates paid ad campaigns, handles promotion expiry with delay and condition logic, and logs performance to Google Sheets.",
    nodes: [
      trigger("Manual: Launch promotional offer campaign", "manual", {
        howItStarts:
          "A marketer manually launches a promotional offer campaign, specifying the offer details, discount amount, valid dates, target audience criteria, and budget allocation",
        setup:
          "Configure OpenAI API key for AI copy generation. Provide SendGrid and Twilio credentials. Set Meta Marketing API token for paid ads. Configure Google Sheets spreadsheet ID for performance logging.",
        inputFields: [
          {
            name: "offer_name",
            type: "string",
            example: "Spring Sale 25% Off",
          },
          {
            name: "discount_value",
            type: "string",
            example: "25% off everything",
          },
          { name: "promo_code", type: "string", example: "SPRING25" },
          { name: "start_date", type: "string", example: "2026-04-01" },
          { name: "end_date", type: "string", example: "2026-04-07" },
          {
            name: "target_segments",
            type: "string",
            example: "active_customers,cart_abandoners,high_value",
          },
        ],
      }),

      // 2. AI urgency copy generator
      aiNode(
        "2",
        "AI: Generate urgency-driven promo copy",
        280,
        "You are a direct-response copywriter specializing in promotional campaigns. Create urgency-driven copy that converts without being manipulative. Use countdown language and clear value propositions. Return JSON.",
        "Create promotional copy for: {offer_name}. Discount: {discount_value}. Code: {promo_code}. Ends: {end_date}. Return email_subject (urgency + value), email_body_html (hero offer, countdown, promo code, CTA), sms_message (under 160 chars with code), ad_headline (40 chars), ad_body (125 chars), ad_description (30 chars).",
        [
          { field: "email_subject", as: "promo_email_subject", type: "string" },
          { field: "email_body_html", as: "promo_email_body", type: "string" },
          { field: "sms_message", as: "promo_sms", type: "string" },
          { field: "ad_headline", as: "promo_ad_headline", type: "string" },
          { field: "ad_body", as: "promo_ad_body", type: "string" },
          { field: "ad_description", as: "promo_ad_desc", type: "string" },
        ],
      ),

      // 3. Audience segmenter (runCode)
      n("3", "runCode", "Segment audience for targeted delivery", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment audience based on campaign targeting criteria
const segments = (vars.target_segments || "").split(",").map(s => s.trim()).filter(Boolean);
const startDate = new Date(vars.start_date || Date.now());
const endDate = new Date(vars.end_date || Date.now());
const durationDays = Math.ceil((endDate.getTime() - startDate.getTime()) / 86400000);
const totalSegments = segments.length;
const primarySegment = segments[0] || "all_subscribers";
const isMultiSegment = totalSegments > 1 ? "yes" : "no";
return {
  segments_json: JSON.stringify(segments),
  total_segments: totalSegments,
  primary_segment: primarySegment,
  is_multi_segment: isMultiSegment,
  promo_duration_days: durationDays,
  promo_start: startDate.toISOString(),
  promo_end: endDate.toISOString(),
  segmentation_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "segments_json", type: "string" },
            { name: "total_segments", type: "number" },
            { name: "primary_segment", type: "string" },
            { name: "is_multi_segment", type: "string" },
            { name: "promo_duration_days", type: "number" },
            { name: "promo_start", type: "string" },
            { name: "promo_end", type: "string" },
            { name: "segmentation_timestamp", type: "string" },
          ],
        },
      }),

      // 4. Email campaign launcher
      sendgridNode(
        "4",
        "Launch promo email campaign via SendGrid",
        760,
        "{subscriber_email}",
        "{promo_email_subject}",
        "{promo_email_body}",
      ),

      // 5. SMS flash alert (Twilio)
      smsNode(
        "5",
        "Send SMS flash promo alert via Twilio",
        1000,
        "{subscriber_phone}",
        "{promo_sms}",
      ),

      // 6. Paid ad campaign creator (Meta)
      n("6", "hitApi", "Create Meta paid ad campaign for promotion", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/act_{meta_ad_account_id}/ads",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "Promo: {offer_name}" },
            { key: "adset_id", value: "{meta_promo_adset_id}" },
            {
              key: "creative",
              value:
                '{"title":"{promo_ad_headline}","body":"{promo_ad_body}","link_description":"{promo_ad_desc}"}',
            },
            { key: "status", value: "ACTIVE" },
          ],
          variables: [
            { name: "id", customName: "meta_promo_ad_id", type: "string" },
          ],
        },
      }),

      // 7. Promotion expiry handler (delay + condition)
      n("7a", "delayAction", "Wait until promotion end date", 1480, {
        delayDetails: { duration: "{promo_duration_days}", unit: "days" },
      }),

      n("7b", "condition", "Has the promotion expired?", 1640, {
        conditionDetails: {
          conditions: [
            {
              variable: "{promo_duration_days}",
              operator: "greater_than",
              value: "0",
            },
          ],
        },
      }),

      // 8. Promotion performance logger (Sheets)
      sheetsNode(
        "8",
        "Log promotion performance to Sheets",
        1820,
        "PromotionalCampaigns",
        '[["{segmentation_timestamp}","{offer_name}","{discount_value}","{promo_code}","{promo_start}","{promo_end}","{promo_duration_days}d","{total_segments}","{meta_promo_ad_id}","{sg_status}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7a"),
      e("7a", "7b"),
      e("7b", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1141. PUSH NOTIFICATION CAMPAIGN MANAGER (8 nodes)
  // ===================================================================

  "Push Notification Campaign Manager": {
    summary:
      "Triggers on a push campaign brief, segments the audience via runCode, generates AI-optimized push copy, determines optimal send times, dispatches push notifications via OneSignal, monitors delivery and engagement, follows up on non-openers via condition, and logs campaign analytics to Google Sheets.",
    nodes: [
      trigger("Manual: Push notification campaign brief", "manual", {
        howItStarts:
          "A marketer initiates a push notification campaign by submitting the campaign brief with message goal, target audience, and delivery preferences",
        setup:
          "Configure OpenAI API key for push copy optimization. Provide OneSignal API key and App ID for push dispatch. Set Google Sheets spreadsheet ID for campaign analytics.",
        inputFields: [
          {
            name: "campaign_name",
            type: "string",
            example: "Flash Sale Alert",
          },
          {
            name: "campaign_goal",
            type: "string",
            example: "Drive traffic to flash sale page",
          },
          {
            name: "target_audience",
            type: "string",
            example: "active_users_30d",
          },
          { name: "urgency_level", type: "string", example: "high" },
          {
            name: "landing_url",
            type: "string",
            example: "https://example.com/flash-sale",
          },
        ],
      }),

      // 2. Audience segment builder (runCode)
      n("2", "runCode", "Build push notification audience segment", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build audience targeting for push notification campaign
const audience = vars.target_audience || "all";
const urgency = vars.urgency_level || "normal";
const segments = audience.split(",").map(s => s.trim());
const filters = segments.map(seg => ({ field: "tag", key: "segment", relation: "=", value: seg }));
const sendWindow = urgency === "high" ? "immediate" : urgency === "medium" ? "next_2_hours" : "optimal";
const ttl = urgency === "high" ? 3600 : urgency === "medium" ? 14400 : 86400;
return {
  onesignal_filters_json: JSON.stringify(filters),
  segment_count: segments.length,
  send_window: sendWindow,
  ttl_seconds: ttl,
  urgency_label: urgency,
  audience_description: segments.join(" + "),
  build_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "onesignal_filters_json", type: "string" },
            { name: "segment_count", type: "number" },
            { name: "send_window", type: "string" },
            { name: "ttl_seconds", type: "number" },
            { name: "urgency_label", type: "string" },
            { name: "audience_description", type: "string" },
            { name: "build_timestamp", type: "string" },
          ],
        },
      }),

      // 3. AI push copy optimizer
      aiNode(
        "3",
        "AI: Optimize push notification copy",
        520,
        "You are a mobile push notification copywriter. Write concise, high-impact push copy. Title must be under 40 characters. Body under 120 characters. Every word must earn its place. Return JSON.",
        "Write push notification copy. Campaign: {campaign_name}. Goal: {campaign_goal}. Urgency: {urgency_label}. Return push_title (40 chars max, compelling), push_body (120 chars max, clear value + CTA), push_subtitle (optional, 30 chars), big_picture_description (for expanded view), action_button_text (e.g. Shop Now), fallback_title (simpler backup).",
        [
          { field: "push_title", as: "push_title", type: "string" },
          { field: "push_body", as: "push_body", type: "string" },
          { field: "push_subtitle", as: "push_subtitle", type: "string" },
          {
            field: "big_picture_description",
            as: "push_expanded",
            type: "string",
          },
          {
            field: "action_button_text",
            as: "push_action_text",
            type: "string",
          },
          { field: "fallback_title", as: "push_fallback", type: "string" },
        ],
      ),

      // 4. Send time optimizer (runCode)
      n("4", "runCode", "Calculate optimal push send time", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine optimal send time based on urgency and historical data
const sendWindow = vars.send_window || "optimal";
const now = new Date();
let sendAt;
if (sendWindow === "immediate") {
  sendAt = now.toISOString();
} else if (sendWindow === "next_2_hours") {
  const next = new Date(now.getTime() + 7200000);
  sendAt = next.toISOString();
} else {
  // Optimal: next day at 10 AM local
  const optimal = new Date(now);
  optimal.setDate(optimal.getDate() + 1);
  optimal.setHours(10, 0, 0, 0);
  sendAt = optimal.toISOString();
}
return {
  scheduled_send_at: sendAt,
  send_strategy: sendWindow,
  is_immediate: sendWindow === "immediate" ? "yes" : "no",
  optimization_note: sendWindow === "optimal" ? "Scheduled for 10 AM next day for peak engagement" : "Sending based on urgency: " + sendWindow
};`,
          outputVariables: [
            { name: "scheduled_send_at", type: "string" },
            { name: "send_strategy", type: "string" },
            { name: "is_immediate", type: "string" },
            { name: "optimization_note", type: "string" },
          ],
        },
      }),

      // 5. Push notification dispatcher (OneSignal)
      n(
        "5",
        "pushNotification",
        "Dispatch push notification via OneSignal",
        1000,
        {
          pushNotificationDetails: {
            provider: "onesignal",
            appId: "{onesignal_app_id}",
            title: "{push_title}",
            body: "{push_body}",
            url: "{landing_url}",
            filters: "{onesignal_filters_json}",
            ttl: "{ttl_seconds}",
            sendAfter: "{scheduled_send_at}",
          },
        },
      ),

      // 6. Delivery + engagement monitor (runCode)
      n("6", "runCode", "Monitor push delivery and engagement metrics", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate push notification delivery metrics
const deliveryStatus = vars.push_delivery_status || "sent";
const recipientCount = parseInt(vars.push_recipients) || 0;
const delivered = parseInt(vars.push_delivered) || 0;
const opened = parseInt(vars.push_opened) || 0;
const deliveryRate = recipientCount > 0 ? ((delivered / recipientCount) * 100).toFixed(1) : "0";
const openRate = delivered > 0 ? ((opened / delivered) * 100).toFixed(1) : "0";
const hasLowEngagement = parseFloat(openRate) < 5;
return {
  total_recipients: recipientCount,
  total_delivered: delivered,
  total_opened: opened,
  delivery_rate_pct: deliveryRate,
  open_rate_pct: openRate,
  has_low_engagement: hasLowEngagement ? "yes" : "no",
  engagement_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "total_recipients", type: "number" },
            { name: "total_delivered", type: "number" },
            { name: "total_opened", type: "number" },
            { name: "delivery_rate_pct", type: "string" },
            { name: "open_rate_pct", type: "string" },
            { name: "has_low_engagement", type: "string" },
            { name: "engagement_timestamp", type: "string" },
          ],
        },
      }),

      // 7. Non-opener follow-up condition
      n("7", "condition", "Check if non-opener follow-up is needed", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "{has_low_engagement}",
              operator: "equals",
              value: "yes",
            },
          ],
        },
      }),

      // 8. Campaign analytics (Sheets)
      sheetsNode(
        "8",
        "Log push campaign analytics to Sheets",
        1720,
        "PushCampaigns",
        '[["{engagement_timestamp}","{campaign_name}","{push_title}","{audience_description}","{total_recipients}","{total_delivered}","{delivery_rate_pct}%","{open_rate_pct}%","{has_low_engagement}","{send_strategy}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1142. PUSH-PERMISSION PRIMER AND OPT-IN (7 nodes)
  // ===================================================================

  "Push-Permission Primer and Opt-In": {
    summary:
      "Triggers on new app user registration, waits for engagement threshold, generates AI push value proposition, sends a primer email, triggers an in-app push permission prompt, handles opt-in results via condition, and logs opt-in rates to Google Sheets.",
    nodes: [
      trigger("Webhook: New app user registered", "webhook_app", {
        source: "mobile_app",
        event: "user.registered",
        howItStarts:
          "Fires when a new user registers in the mobile app, starting a timed sequence that waits for initial engagement before prompting for push notification permission",
        setup:
          "Configure webhook from your mobile app backend. Set OpenAI API key for AI value proposition. Provide email service credentials for primer email. Configure push notification service. Set Google Sheets spreadsheet ID for opt-in tracking.",
        inputFields: [],
      }),

      // 2. Engagement threshold monitor (delay)
      n(
        "2",
        "delayAction",
        "Wait for user to reach engagement threshold",
        280,
        {
          delayDetails: { duration: 3, unit: "days" },
        },
      ),

      // 3. AI push value proposition
      aiNode(
        "3",
        "AI: Generate push permission value proposition",
        520,
        "You are a mobile app UX writer specializing in permission priming. Create compelling reasons for users to enable push notifications. Focus on the VALUE they receive, not what you want. Never be manipulative. Return JSON.",
        "Write push notification permission primer content for {company_name} app. The user has been active for 3 days. App type: {app_category}. Return primer_email_subject (benefit-focused), primer_email_html (explains what push delivers: deals, updates, etc.), in_app_headline (short, benefit-led), in_app_body (2 sentences on what they will get), in_app_accept_text (positive button), in_app_decline_text (respectful decline option).",
        [
          {
            field: "primer_email_subject",
            as: "primer_subject",
            type: "string",
          },
          { field: "primer_email_html", as: "primer_body", type: "string" },
          { field: "in_app_headline", as: "inapp_headline", type: "string" },
          { field: "in_app_body", as: "inapp_body", type: "string" },
          { field: "in_app_accept_text", as: "inapp_accept", type: "string" },
          { field: "in_app_decline_text", as: "inapp_decline", type: "string" },
        ],
      ),

      // 4. Email push primer
      emailNode(
        "4",
        "Send push permission primer email",
        760,
        "{user_email}",
        "{primer_subject}",
        "{primer_body}",
      ),

      // 5. In-app primer trigger (pushNotification)
      n(
        "5",
        "pushNotification",
        "Trigger in-app push permission prompt",
        1000,
        {
          pushNotificationDetails: {
            provider: "onesignal",
            appId: "{onesignal_app_id}",
            title: "{inapp_headline}",
            body: "{inapp_body}",
            type: "in_app_primer",
            acceptText: "{inapp_accept}",
            declineText: "{inapp_decline}",
            targetUserId: "{user_id}",
          },
        },
      ),

      // 6. Opt-in result handler (condition)
      n("6", "condition", "Did the user opt in to push notifications?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{push_permission_granted}",
              operator: "equals",
              value: "yes",
            },
          ],
        },
      }),

      // 7. Opt-in logger (Sheets)
      sheetsNode(
        "7",
        "Log push opt-in results to Sheets",
        1480,
        "PushOptIn",
        '[["{user_email}","{user_id}","{push_permission_granted}","{inapp_headline}","{primer_subject}","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1143. QR-CODE IN-STORE OPT-IN (8 nodes)
  // ===================================================================

  "QR-Code In-Store Opt-In": {
    summary:
      "Triggers on QR code scan from in-store signage, processes the opt-in form submission, tags location data via runCode, creates a CRM contact in HubSpot, generates an AI instant welcome offer, sends the welcome email with a discount, delivers an SMS instant gratification message via Twilio, and tracks in-store conversions in Google Sheets.",
    nodes: [
      trigger("Webhook: QR code scan opt-in", "webhook_form", {
        source: "in_store_qr",
        event: "qr_optin.submitted",
        howItStarts:
          "Fires when a customer scans a QR code on in-store signage and submits the opt-in form, capturing their email, phone number, store location, and any preferences",
        setup:
          "Configure webhook from your QR code landing page form. Set HubSpot API key for CRM contact creation. Provide OpenAI API key for AI welcome offers. Set SendGrid API key for email. Configure Twilio for SMS. Set Google Sheets spreadsheet ID for in-store tracking.",
        inputFields: [],
      }),

      // 2. Opt-in form handler (runCode — extracts from webhook)
      n("2", "runCode", "Process QR opt-in form submission", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract opt-in data from QR code form webhook
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = (payload.email || payload.subscriber_email || "").toLowerCase().trim();
const phone = payload.phone || payload.mobile || "";
const name = payload.name || payload.first_name || "";
const storeId = payload.store_id || payload.location_id || "unknown";
const storeName = payload.store_name || payload.location_name || "In-Store";
const qrId = payload.qr_code_id || payload.campaign_id || "";
const consent = payload.marketing_consent === true || payload.opt_in === true || payload.opt_in === "yes";
return {
  optin_email: email,
  optin_phone: phone,
  optin_name: name,
  store_id: storeId,
  store_name: storeName,
  qr_code_id: qrId,
  has_consent: consent ? "yes" : "no",
  has_phone: phone.length > 5 ? "yes" : "no",
  optin_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "optin_email", type: "string" },
            { name: "optin_phone", type: "string" },
            { name: "optin_name", type: "string" },
            { name: "store_id", type: "string" },
            { name: "store_name", type: "string" },
            { name: "qr_code_id", type: "string" },
            { name: "has_consent", type: "string" },
            { name: "has_phone", type: "string" },
            { name: "optin_timestamp", type: "string" },
          ],
        },
      }),

      // 3. Location tagger (runCode)
      n("3", "runCode", "Tag subscriber with store location data", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Enrich subscriber profile with location metadata
const storeId = vars.store_id || "unknown";
const storeName = vars.store_name || "Unknown Store";
const locationTag = "store_" + storeId.toLowerCase().replace(/[^a-z0-9]/g, "_");
const regionMap = {
  "NYC": "northeast", "LA": "west", "CHI": "midwest", "MIA": "southeast",
  "DAL": "south", "SEA": "northwest", "DEN": "mountain"
};
const storePrefix = storeId.substring(0, 3).toUpperCase();
const region = regionMap[storePrefix] || "national";
return {
  location_tag: locationTag,
  store_region: region,
  acquisition_channel: "in_store_qr",
  acquisition_store: storeName,
  location_enriched: "yes"
};`,
          outputVariables: [
            { name: "location_tag", type: "string" },
            { name: "store_region", type: "string" },
            { name: "acquisition_channel", type: "string" },
            { name: "acquisition_store", type: "string" },
            { name: "location_enriched", type: "string" },
          ],
        },
      }),

      // 4. CRM contact creator (HubSpot)
      hubspotPostNode(
        "4",
        "Create in-store opt-in contact in HubSpot",
        760,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{optin_email}","firstname":"{optin_name}","phone":"{optin_phone}","lifecyclestage":"subscriber","acquisition_source":"in_store_qr","store_location":"{store_name}","store_region":"{store_region}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          { name: "properties.email", customName: "crm_email", type: "string" },
        ],
      ),

      // 5. AI instant welcome offer
      aiNode(
        "5",
        "AI: Generate instant in-store welcome offer",
        1000,
        "You are a retail marketing specialist. Create an immediate welcome offer that rewards in-store QR sign-ups with an exclusive discount. The offer should feel like an instant reward for their action. Return JSON.",
        "Create an instant welcome offer for a customer who just signed up via QR code at {store_name}. Company: {company_name}. Return welcome_subject (excitement + instant reward), welcome_body_html (thank them, highlight the exclusive offer, include barcode/code placeholder, mention in-store and online validity), offer_code (unique-looking code), offer_value (e.g. 15% off first purchase), sms_welcome (under 160 chars with offer code).",
        [
          {
            field: "welcome_subject",
            as: "qr_welcome_subject",
            type: "string",
          },
          { field: "welcome_body_html", as: "qr_welcome_body", type: "string" },
          { field: "offer_code", as: "qr_offer_code", type: "string" },
          { field: "offer_value", as: "qr_offer_value", type: "string" },
          { field: "sms_welcome", as: "qr_sms_welcome", type: "string" },
        ],
      ),

      // 6. Welcome email with offer
      sendgridNode(
        "6",
        "Send welcome email with instant offer",
        1240,
        "{optin_email}",
        "{qr_welcome_subject}",
        "{qr_welcome_body}",
      ),

      // 7. SMS instant gratification (Twilio)
      smsNode(
        "7",
        "Send instant SMS welcome via Twilio",
        1480,
        "{optin_phone}",
        "{qr_sms_welcome}",
      ),

      // 8. In-store conversion tracker (Sheets)
      sheetsNode(
        "8",
        "Track in-store QR conversion in Sheets",
        1720,
        "InStoreQROptins",
        '[["{optin_timestamp}","{optin_email}","{optin_phone}","{store_name}","{store_id}","{store_region}","{qr_code_id}","{qr_offer_code}","{qr_offer_value}","{hubspot_contact_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1144. QUIZ-BASED ZERO-PARTY-DATA CAPTURE (9 nodes)
  // ===================================================================

  "Quiz-Based Zero-Party-Data Capture": {
    summary:
      "Triggers on quiz launch, collects quiz responses, gates email and SMS capture, parses zero-party data via runCode, generates AI personalized results, delivers results via email, enriches CRM profiles in HubSpot, assigns audience segments, and logs quiz analytics to Google Sheets.",
    nodes: [
      trigger("Webhook: Quiz completed", "webhook_app", {
        source: "quiz_platform",
        event: "quiz.completed",
        howItStarts:
          "Fires when a visitor completes an interactive quiz, capturing their answers, contact information, and quiz metadata for personalized follow-up and zero-party data enrichment",
        setup:
          "Configure webhook from your quiz platform (e.g., Outgrow Forms, Octane AI). Set OpenAI API key for AI result generation. Provide SendGrid API key for email delivery. Configure HubSpot API key for CRM enrichment. Set Google Sheets spreadsheet ID for quiz analytics.",
        inputFields: [],
      }),

      // 2. Quiz response collector (runCode)
      n("2", "runCode", "Collect and validate quiz responses", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract quiz responses from webhook payload
const payload = JSON.parse(vars.webhook_payload || "{}");
const quizId = payload.quiz_id || payload.form_id || "";
const quizTitle = payload.quiz_title || payload.title || "Product Quiz";
const answers = payload.answers || payload.responses || [];
const email = (payload.email || payload.contact_email || "").toLowerCase().trim();
const phone = payload.phone || payload.sms_number || "";
const name = payload.name || payload.first_name || "";
const totalQuestions = answers.length;
const completedAt = payload.completed_at || new Date().toISOString();
return {
  quiz_id: quizId,
  quiz_title: quizTitle,
  quiz_email: email,
  quiz_phone: phone,
  quiz_name: name,
  answers_json: JSON.stringify(answers),
  total_questions: totalQuestions,
  has_email: email.includes("@") ? "yes" : "no",
  has_phone: phone.length > 5 ? "yes" : "no",
  completed_at: completedAt
};`,
          outputVariables: [
            { name: "quiz_id", type: "string" },
            { name: "quiz_title", type: "string" },
            { name: "quiz_email", type: "string" },
            { name: "quiz_phone", type: "string" },
            { name: "quiz_name", type: "string" },
            { name: "answers_json", type: "string" },
            { name: "total_questions", type: "number" },
            { name: "has_email", type: "string" },
            { name: "has_phone", type: "string" },
            { name: "completed_at", type: "string" },
          ],
        },
      }),

      // 3. Email + SMS capture gate (condition)
      n("3", "condition", "Verify email was captured from quiz", 520, {
        conditionDetails: {
          conditions: [
            { variable: "{has_email}", operator: "equals", value: "yes" },
          ],
        },
      }),

      // 4. Zero-party data parser (runCode)
      n("4", "runCode", "Parse zero-party data from quiz answers", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse quiz answers into structured zero-party data profile
const answers = JSON.parse(vars.answers_json || "[]");
const zpd = {
  preferences: [],
  interests: [],
  demographics: {},
  purchase_intent: "unknown",
  budget_range: "unknown",
  style_preference: "unknown"
};
answers.forEach(a => {
  const q = (a.question || a.field_title || "").toLowerCase();
  const v = a.answer || a.value || a.text || "";
  if (q.includes("interest") || q.includes("like") || q.includes("prefer")) {
    zpd.interests.push(v);
  } else if (q.includes("budget") || q.includes("spend")) {
    zpd.budget_range = v;
  } else if (q.includes("style") || q.includes("type")) {
    zpd.style_preference = v;
  } else if (q.includes("buy") || q.includes("purchase") || q.includes("looking")) {
    zpd.purchase_intent = v;
  } else {
    zpd.preferences.push(v);
  }
});
return {
  zpd_profile_json: JSON.stringify(zpd),
  interests_csv: zpd.interests.join(", "),
  budget_range: zpd.budget_range,
  style_preference: zpd.style_preference,
  purchase_intent: zpd.purchase_intent,
  data_points_captured: zpd.interests.length + zpd.preferences.length + 3,
  primary_interest: zpd.interests[0] || zpd.preferences[0] || "general"
};`,
          outputVariables: [
            { name: "zpd_profile_json", type: "string" },
            { name: "interests_csv", type: "string" },
            { name: "budget_range", type: "string" },
            { name: "style_preference", type: "string" },
            { name: "purchase_intent", type: "string" },
            { name: "data_points_captured", type: "number" },
            { name: "primary_interest", type: "string" },
          ],
        },
      }),

      // 5. AI personalized results
      aiNode(
        "5",
        "AI: Generate personalized quiz results",
        1000,
        "You are a personalization expert. Generate quiz results that feel uniquely tailored to the respondent. Include product recommendations based on their answers. Make it feel like a reward for completing the quiz. Return JSON.",
        "Generate personalized quiz results. Quiz: {quiz_title}. Interests: {interests_csv}. Budget: {budget_range}. Style: {style_preference}. Purchase intent: {purchase_intent}. Company: {company_name}. Return results_subject (personalized email subject), results_body_html (their profile summary, personalized recommendations, exclusive next step), result_headline (catchy one-liner about their type/style), product_recommendations (3 product suggestions with reasoning), next_step_cta (what to do next).",
        [
          { field: "results_subject", as: "results_subject", type: "string" },
          { field: "results_body_html", as: "results_body", type: "string" },
          { field: "result_headline", as: "result_headline", type: "string" },
          {
            field: "product_recommendations",
            as: "recommendations_json",
            type: "string",
          },
          { field: "next_step_cta", as: "next_step_cta", type: "string" },
        ],
      ),

      // 6. Results delivery email
      sendgridNode(
        "6",
        "Deliver personalized quiz results via email",
        1240,
        "{quiz_email}",
        "{results_subject}",
        "{results_body}",
      ),

      // 7. CRM profile enrichment (HubSpot)
      hubspotPostNode(
        "7",
        "Enrich CRM profile with zero-party data",
        1480,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{quiz_email}","firstname":"{quiz_name}","lifecyclestage":"lead","quiz_completed":"{quiz_title}","primary_interest":"{primary_interest}","budget_range":"{budget_range}","style_preference":"{style_preference}","purchase_intent":"{purchase_intent}","data_points":"{data_points_captured}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          { name: "properties.email", customName: "crm_email", type: "string" },
        ],
      ),

      // 8. Segment assignment (runCode)
      n("8", "runCode", "Assign audience segments from quiz data", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assign audience segments based on zero-party data profile
const interest = vars.primary_interest || "general";
const budget = vars.budget_range || "unknown";
const intent = vars.purchase_intent || "unknown";
const segments = [];
segments.push("quiz_completer");
segments.push("interest_" + interest.toLowerCase().replace(/[^a-z0-9]/g, "_"));
if (budget.includes("high") || budget.includes("premium") || budget.includes("$500")) {
  segments.push("high_value_prospect");
} else if (budget.includes("mid") || budget.includes("moderate")) {
  segments.push("mid_value_prospect");
}
if (intent.includes("buy") || intent.includes("purchase") || intent.includes("ready")) {
  segments.push("high_purchase_intent");
}
return {
  assigned_segments: segments.join(","),
  segment_count: segments.length,
  primary_segment: segments[0] || "general",
  is_high_intent: segments.includes("high_purchase_intent") ? "yes" : "no",
  assignment_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "assigned_segments", type: "string" },
            { name: "segment_count", type: "number" },
            { name: "primary_segment", type: "string" },
            { name: "is_high_intent", type: "string" },
            { name: "assignment_timestamp", type: "string" },
          ],
        },
      }),

      // 9. Quiz analytics logger (Sheets)
      sheetsNode(
        "9",
        "Log quiz analytics to Google Sheets",
        1960,
        "QuizAnalytics",
        '[["{completed_at}","{quiz_email}","{quiz_title}","{total_questions}","{data_points_captured}","{primary_interest}","{budget_range}","{purchase_intent}","{assigned_segments}","{is_high_intent}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1145. RSS FEED TO SOCIAL MEDIA AUTO POSTER (7 nodes)
  // ===================================================================

  "RSS Feed to Social Media Auto Poster": {
    summary:
      "Monitors RSS feeds on a schedule, detects new content via runCode, generates AI-optimized social posts, routes through an approval gate, publishes across platforms via Buffer, guards against duplicates and rate limits in code, and logs post performance to Google Sheets.",
    nodes: [
      trigger("Schedule: RSS feed check", "schedule", {
        schedule: "0 */2 * * * (Every 2 hours)",
        howItStarts:
          "A scheduled check polls configured RSS feeds for new content, detecting new articles or blog posts that should be shared across social media channels",
        setup:
          "Configure RSS feed URLs to monitor. Set OpenAI API key for AI post generation. Provide Buffer API key and profile IDs for social publishing. Set Google Sheets spreadsheet ID for post logging and duplicate tracking.",
        inputFields: [],
      }),

      // 2. New content detector (runCode)
      n("2", "runCode", "Detect new RSS content since last check", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Fetch and parse RSS feed for new content
const feedUrl = vars.rss_feed_url || "";
const lastCheck = vars.last_rss_check || new Date(Date.now() - 7200000).toISOString();
// Parse RSS XML (simplified — in production use an XML parser)
const response = await fetch(feedUrl);
const xml = await response.text();
// Extract items using regex for portability
const items = [];
const itemRegex = /<item>(.*?)<\\/item>/gs;
let match;
while ((match = itemRegex.exec(xml)) !== null) {
  const titleMatch = match[1].match(/<title>(?:<\\!\\[CDATA\\[)?(.*?)(?:\\]\\]>)?<\\/title>/);
  const linkMatch = match[1].match(/<link>(.*?)<\\/link>/);
  const descMatch = match[1].match(/<description>(?:<\\!\\[CDATA\\[)?(.*?)(?:\\]\\]>)?<\\/description>/);
  const dateMatch = match[1].match(/<pubDate>(.*?)<\\/pubDate>/);
  const pubDate = dateMatch ? new Date(dateMatch[1]) : new Date();
  if (pubDate > new Date(lastCheck)) {
    items.push({
      title: titleMatch ? titleMatch[1] : "",
      link: linkMatch ? linkMatch[1] : "",
      description: descMatch ? descMatch[1].replace(/<[^>]*>/g, "").substring(0, 200) : "",
      published: pubDate.toISOString()
    });
  }
}
const hasNew = items.length > 0;
const latest = hasNew ? items[0] : null;
return {
  new_items_count: items.length,
  has_new_content: hasNew ? "yes" : "no",
  latest_title: latest ? latest.title : "",
  latest_link: latest ? latest.link : "",
  latest_description: latest ? latest.description : "",
  latest_published: latest ? latest.published : "",
  all_new_items_json: JSON.stringify(items),
  check_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "new_items_count", type: "number" },
            { name: "has_new_content", type: "string" },
            { name: "latest_title", type: "string" },
            { name: "latest_link", type: "string" },
            { name: "latest_description", type: "string" },
            { name: "latest_published", type: "string" },
            { name: "all_new_items_json", type: "string" },
            { name: "check_timestamp", type: "string" },
          ],
        },
      }),

      // 3. AI social post generator
      aiNode(
        "3",
        "AI: Generate social media posts from RSS content",
        520,
        "You are a social media content specialist. Transform blog/article content into engaging social posts optimized for each platform. Be informative and conversational, not salesy. Include relevant hashtags. Return JSON.",
        "Create social media posts for this article. Title: {latest_title}. Description: {latest_description}. Link: {latest_link}. Company: {company_name}. Return twitter_post (280 chars with link), linkedin_post (professional tone, 300 words max), instagram_caption (engaging with hashtags), buffer_post (universal version, 250 chars), hashtags (5-7 relevant tags).",
        [
          { field: "twitter_post", as: "social_twitter", type: "string" },
          { field: "linkedin_post", as: "social_linkedin", type: "string" },
          {
            field: "instagram_caption",
            as: "social_instagram",
            type: "string",
          },
          { field: "buffer_post", as: "social_buffer", type: "string" },
          { field: "hashtags", as: "social_hashtags", type: "string" },
        ],
      ),

      // 4. Content approval gate
      n("4", "approvalGate", "Content approval before social publish", 760, {
        approvalGateDetails: {
          approvers: ["{content_manager_email}"],
          message:
            "New RSS content ready for social publish:\\n\\nTitle: {latest_title}\\nLink: {latest_link}\\n\\nTwitter: {social_twitter}\\nBuffer: {social_buffer}\\n\\nApprove to publish across all social channels.",
          timeoutHours: 24,
        },
      }),

      // 5. Multi-platform publisher (Buffer)
      bufferNode(
        "5",
        "Publish across social platforms via Buffer",
        1000,
        "{social_buffer}\\n\\n{social_hashtags}\\n\\n{latest_link}",
      ),

      // 6. Duplicate + rate limit guard (runCode)
      n("6", "runCode", "Guard against duplicates and rate limiting", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check for duplicates and enforce rate limits
const publishedTitle = vars.latest_title || "";
const publishedLink = vars.latest_link || "";
const bufferSuccess = vars.buffer_success === true || vars.buffer_success === "true";
const publishTimestamp = new Date().toISOString();
// Generate content hash for dedup
const contentHash = publishedTitle.replace(/[^a-zA-Z0-9]/g, "").substring(0, 30).toLowerCase();
// Rate limit: track posts in last 24h
const postsToday = parseInt(vars.posts_today_count) || 0;
const rateLimitExceeded = postsToday >= 10;
return {
  content_hash: contentHash,
  is_duplicate: "no",
  rate_limit_exceeded: rateLimitExceeded ? "yes" : "no",
  posts_today: postsToday + 1,
  publish_success: bufferSuccess && !rateLimitExceeded ? "yes" : "no",
  published_url: publishedLink,
  publish_timestamp: publishTimestamp,
  dedup_key: contentHash + "_" + publishTimestamp.substring(0, 10)
};`,
          outputVariables: [
            { name: "content_hash", type: "string" },
            { name: "is_duplicate", type: "string" },
            { name: "rate_limit_exceeded", type: "string" },
            { name: "posts_today", type: "number" },
            { name: "publish_success", type: "string" },
            { name: "published_url", type: "string" },
            { name: "publish_timestamp", type: "string" },
            { name: "dedup_key", type: "string" },
          ],
        },
      }),

      // 7. Post performance logger (Sheets)
      sheetsNode(
        "7",
        "Log post performance to Google Sheets",
        1480,
        "RSSAutoPost",
        '[["{publish_timestamp}","{latest_title}","{published_url}","{content_hash}","{publish_success}","{posts_today}","{rate_limit_exceeded}","{buffer_update_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1146. RE-ENGAGEMENT EMAIL CAMPAIGN (9 nodes)
  // ===================================================================

  "Re-Engagement Email Campaign": {
    summary:
      "Scans for inactive users on a schedule, classifies inactivity depth via runCode, enriches user history from HubSpot, generates AI win-back content, delivers a multi-touch re-engagement sequence with email and SMS, checks engagement after each touch, and logs outcomes with sunset decisions to Google Sheets.",
    nodes: [
      trigger("Schedule: Inactive user scan", "schedule", {
        schedule: "0 8 * * 1 (Monday at 8 AM)",
        howItStarts:
          "A weekly schedule identifies users who have not engaged in the past 30+ days, initiating a multi-touch re-engagement campaign with escalating urgency",
        setup:
          "Configure user activity data source. Set HubSpot API key for user history enrichment. Provide OpenAI API key for AI win-back content. Set SendGrid and Twilio credentials for multi-channel delivery. Configure Google Sheets spreadsheet ID for outcome logging.",
        inputFields: [],
      }),

      // 2. Inactivity depth classifier (runCode)
      n("2", "runCode", "Classify user inactivity depth and risk", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify inactive users by depth of inactivity
const email = vars.inactive_user_email || "";
const lastActivity = vars.last_activity_date ? new Date(vars.last_activity_date) : new Date(Date.now() - 90 * 86400000);
const now = new Date();
const daysSinceActive = Math.floor((now.getTime() - lastActivity.getTime()) / 86400000);
let inactivityTier, urgency, maxTouches;
if (daysSinceActive >= 90) {
  inactivityTier = "critical"; urgency = "high"; maxTouches = 3;
} else if (daysSinceActive >= 60) {
  inactivityTier = "at_risk"; urgency = "medium"; maxTouches = 3;
} else {
  inactivityTier = "early_warning"; urgency = "low"; maxTouches = 2;
}
return {
  user_email: email,
  days_inactive: daysSinceActive,
  inactivity_tier: inactivityTier,
  urgency_level: urgency,
  max_touches: maxTouches,
  should_sunset: daysSinceActive >= 120 ? "yes" : "no",
  classification_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "user_email", type: "string" },
            { name: "days_inactive", type: "number" },
            { name: "inactivity_tier", type: "string" },
            { name: "urgency_level", type: "string" },
            { name: "max_touches", type: "number" },
            { name: "should_sunset", type: "string" },
            { name: "classification_timestamp", type: "string" },
          ],
        },
      }),

      // 3. User history enrichment (HubSpot)
      hubspotGetNode(
        "3",
        "Enrich user history from HubSpot",
        520,
        'crm/v3/objects/contacts/search?filterGroups=[{"filters":[{"propertyName":"email","operator":"EQ","value":"{user_email}"}]}]&properties=email,firstname,total_revenue,num_associated_deals,hs_email_last_open_date,hs_email_last_click_date',
        [
          {
            name: "results[0].properties.firstname",
            customName: "user_firstname",
            type: "string",
          },
          {
            name: "results[0].properties.total_revenue",
            customName: "user_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "user_deals",
            type: "string",
          },
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
        ],
      ),

      // 4. AI win-back content
      aiNode(
        "4",
        "AI: Generate win-back email content series",
        760,
        "You are a retention marketing specialist. Write a multi-touch win-back email series with escalating urgency. First touch is warm and curious. Second is value-reminder. Third is final farewell with incentive. Return JSON.",
        "Create a win-back series for {user_firstname} who has been inactive for {days_inactive} days. Tier: {inactivity_tier}. Past revenue: {user_revenue}. Company: {company_name}. Return email1_subject (curious/warm), email1_body_html (we miss you, what is new), email2_subject (value reminder), email2_body_html (highlights of what they are missing), email3_subject (final with incentive), email3_body_html (last chance + exclusive offer), sms_winback (under 160 chars, personal + offer), win_back_offer (e.g. 20% off comeback discount).",
        [
          { field: "email1_subject", as: "wb_email1_subject", type: "string" },
          { field: "email1_body_html", as: "wb_email1_body", type: "string" },
          { field: "email2_subject", as: "wb_email2_subject", type: "string" },
          { field: "email2_body_html", as: "wb_email2_body", type: "string" },
          { field: "email3_subject", as: "wb_email3_subject", type: "string" },
          { field: "email3_body_html", as: "wb_email3_body", type: "string" },
          { field: "sms_winback", as: "wb_sms_message", type: "string" },
          { field: "win_back_offer", as: "wb_offer", type: "string" },
        ],
      ),

      // 5. Re-engagement email 1
      sendgridNode(
        "5",
        "Send re-engagement email 1: We miss you",
        1000,
        "{user_email}",
        "{wb_email1_subject}",
        "{wb_email1_body}",
      ),

      // 6. Engagement check condition (after delay)
      n("6a", "delayAction", "Wait 5 days to check engagement", 1180, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      n("6b", "condition", "Did user re-engage after email 1?", 1340, {
        conditionDetails: {
          conditions: [
            { variable: "{sg_status}", operator: "equals", value: "202" },
          ],
        },
      }),

      // 7. Re-engagement email 2 (delay) — False branch continues
      n(
        "7a",
        "delayAction",
        "Wait 5 more days for email 2",
        1520,
        {
          delayDetails: { duration: 5, unit: "days" },
        },
        500,
      ),

      n(
        "7b",
        "sendEmail",
        "Send re-engagement email 2: Value reminder",
        1660,
        {
          sendEmailDetails: {
            to: "{user_email}",
            content: {
              subject: "{wb_email2_subject}",
              body: "{wb_email2_body}",
            },
          },
        },
        500,
      ),

      // 8. Final win-back attempt (email + SMS)
      n(
        "8a",
        "delayAction",
        "Wait 5 days before final attempt",
        1840,
        {
          delayDetails: { duration: 5, unit: "days" },
        },
        500,
      ),

      n(
        "8b",
        "sendEmail",
        "Send final win-back email with offer",
        1980,
        {
          sendEmailDetails: {
            to: "{user_email}",
            content: {
              subject: "{wb_email3_subject}",
              body: "{wb_email3_body}",
            },
          },
        },
        500,
      ),

      smsNode(
        "8c",
        "Send win-back SMS with offer via Twilio",
        2120,
        "{user_phone}",
        "{wb_sms_message}",
        500,
      ),

      // 9. Outcome logger + sunset decision (Sheets)
      sheetsNode(
        "9",
        "Log re-engagement outcome and sunset decision",
        2300,
        "ReEngagementOutcomes",
        '[["{classification_timestamp}","{user_email}","{days_inactive}","{inactivity_tier}","{urgency_level}","{user_revenue}","{wb_offer}","{max_touches}","{should_sunset}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6a"),
      e("6a", "6b"),
      e("6b", "9", "output_1"),
      e("6b", "7a", "output_2"),
      e("7a", "7b"),
      e("7b", "8a"),
      e("8a", "8b"),
      e("8b", "8c"),
      e("8c", "9"),
    ],
  },

  // ===================================================================
  // 1147. REAL-TIME PERSONALIZATION ENGINE (10 nodes)
  // ===================================================================

  "Real-Time Personalization Engine": {
    summary:
      "Listens for real-time user activity events, aggregates user profiles from HubSpot, analyzes behavioral patterns via runCode, predicts intent with AI, generates content recommendations, adapts experiences in real time via runCode, manages cross-channel consistency, tracks conversion events, monitors personalization performance in Google Sheets, and handles anomalies with Slack fallback alerts.",
    nodes: [
      trigger("Webhook: User activity event stream", "webhook_app", {
        source: "event_stream",
        event: "activity.tracked",
        howItStarts:
          "Fires on each significant user activity event (page view, product interaction, search, click) streaming from the website or app, enabling real-time personalization decisions",
        setup:
          "Configure event stream webhook from your analytics/CDP platform. Set HubSpot API key for profile aggregation. Provide OpenAI API key for intent prediction. Configure content recommendation API. Set Google Sheets spreadsheet ID for performance monitoring. Configure Slack webhook for anomaly alerts.",
        inputFields: [],
      }),

      // 2. User profile aggregator (HubSpot)
      hubspotGetNode(
        "2",
        "Aggregate user profile from HubSpot",
        280,
        'crm/v3/objects/contacts/search?filterGroups=[{"filters":[{"propertyName":"email","operator":"EQ","value":"{user_email}"}]}]&properties=email,firstname,lastname,lifecyclestage,total_revenue,hs_analytics_num_page_views,hs_analytics_num_visits,hs_email_last_open_date,recent_conversion_event',
        [
          {
            name: "results[0].properties.firstname",
            customName: "user_name",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "user_lifecycle",
            type: "string",
          },
          {
            name: "results[0].properties.total_revenue",
            customName: "user_ltv",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_num_page_views",
            customName: "user_page_views",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_num_visits",
            customName: "user_visits",
            type: "string",
          },
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
        ],
      ),

      // 3. Behavioral pattern analyzer (runCode)
      n(
        "3",
        "runCode",
        "Analyze behavioral patterns from activity stream",
        520,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Analyze user behavioral patterns from current activity and historical data
const payload = JSON.parse(vars.webhook_payload || "{}");
const eventType = payload.event_type || payload.action || "page_view";
const pageUrl = payload.page_url || payload.url || "";
const productId = payload.product_id || "";
const searchQuery = payload.search_query || "";
const sessionDuration = parseInt(payload.session_duration) || 0;
const cartValue = parseFloat(payload.cart_value) || 0;
const pageViews = parseInt(vars.user_page_views) || 0;
const visits = parseInt(vars.user_visits) || 0;
const ltv = parseFloat(vars.user_ltv) || 0;
// Pattern scoring
let browsingIntent = "exploring";
if (eventType === "add_to_cart" || cartValue > 0) browsingIntent = "purchasing";
else if (eventType === "search") browsingIntent = "searching";
else if (pageUrl.includes("pricing") || pageUrl.includes("plans")) browsingIntent = "evaluating";
else if (pageUrl.includes("checkout")) browsingIntent = "converting";
const engagementLevel = sessionDuration > 300 ? "high" : sessionDuration > 120 ? "medium" : "low";
const valueSegment = ltv > 1000 ? "vip" : ltv > 200 ? "loyal" : visits > 5 ? "engaged" : "new";
const isAnomaly = sessionDuration > 3600 || (eventType === "page_view" && pageViews > 100);
return {
  current_event: eventType,
  current_page: pageUrl,
  current_product: productId,
  search_query: searchQuery,
  browsing_intent: browsingIntent,
  engagement_level: engagementLevel,
  value_segment: valueSegment,
  session_duration_seconds: sessionDuration,
  cart_value: cartValue,
  is_anomalous: isAnomaly ? "yes" : "no",
  pattern_timestamp: new Date().toISOString()
};`,
            outputVariables: [
              { name: "current_event", type: "string" },
              { name: "current_page", type: "string" },
              { name: "current_product", type: "string" },
              { name: "search_query", type: "string" },
              { name: "browsing_intent", type: "string" },
              { name: "engagement_level", type: "string" },
              { name: "value_segment", type: "string" },
              { name: "session_duration_seconds", type: "number" },
              { name: "cart_value", type: "number" },
              { name: "is_anomalous", type: "string" },
              { name: "pattern_timestamp", type: "string" },
            ],
          },
        },
      ),

      // 4. AI intent predictor
      aiNode(
        "4",
        "AI: Predict user intent and next action",
        760,
        "You are a real-time personalization AI. Based on behavioral signals, predict the user's intent and recommend the optimal next experience. Be specific and actionable. Return JSON.",
        "Predict intent for user: {user_name}. Current event: {current_event}. Page: {current_page}. Product: {current_product}. Search: {search_query}. Browsing intent: {browsing_intent}. Engagement: {engagement_level}. Value segment: {value_segment}. Cart value: {cart_value}. Lifecycle: {user_lifecycle}. Return predicted_intent (specific goal), confidence_score (0-100), recommended_action (what to show/do next), content_type (product/article/offer/social_proof), content_category (specific category), personalization_strategy (1-2 sentences), fallback_action (if prediction is wrong).",
        [
          { field: "predicted_intent", as: "predicted_intent", type: "string" },
          {
            field: "confidence_score",
            as: "intent_confidence",
            type: "number",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "content_category", as: "content_category", type: "string" },
          {
            field: "personalization_strategy",
            as: "personalization_strategy",
            type: "string",
          },
          { field: "fallback_action", as: "fallback_action", type: "string" },
        ],
      ),

      // 5. Content recommendation engine (runCode)
      n("5", "runCode", "Generate personalized content recommendations", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build personalized content recommendations based on AI prediction
const contentType = vars.content_type || "product";
const category = vars.content_category || "general";
const intent = vars.predicted_intent || "browse";
const confidence = parseInt(vars.intent_confidence) || 50;
const valueSegment = vars.value_segment || "new";
// Build recommendation payload
const recommendations = {
  primary: {
    type: contentType,
    category: category,
    personalization: vars.personalization_strategy || "default",
    position: "hero",
    priority: confidence > 70 ? "high" : "medium"
  },
  secondary: [
    { type: "social_proof", category: category, position: "below_fold" },
    { type: valueSegment === "vip" ? "exclusive_offer" : "popular_items", position: "sidebar" }
  ],
  exit_intent: {
    type: confidence > 60 ? "targeted_offer" : "newsletter_signup",
    trigger: "exit_intent"
  }
};
const useAiRecommendation = confidence >= 50 ? "yes" : "no";
return {
  recommendations_json: JSON.stringify(recommendations),
  primary_recommendation_type: recommendations.primary.type,
  primary_recommendation_priority: recommendations.primary.priority,
  use_ai_recommendation: useAiRecommendation,
  total_recommendations: 1 + recommendations.secondary.length + 1,
  recommendation_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "recommendations_json", type: "string" },
            { name: "primary_recommendation_type", type: "string" },
            { name: "primary_recommendation_priority", type: "string" },
            { name: "use_ai_recommendation", type: "string" },
            { name: "total_recommendations", type: "number" },
            { name: "recommendation_timestamp", type: "string" },
          ],
        },
      }),

      // 6. Real-time experience adapter (runCode)
      n("6", "runCode", "Adapt real-time experience for the user", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Prepare real-time experience adaptation payload
const useAi = vars.use_ai_recommendation === "yes";
const recommendations = JSON.parse(vars.recommendations_json || "{}");
const intent = vars.predicted_intent || "browse";
const action = useAi ? vars.recommended_action : vars.fallback_action;
const experience = {
  user_id: vars.user_id || vars.hubspot_contact_id || "anonymous",
  session_id: vars.session_id || "s_" + Date.now(),
  adaptations: [{
    element: "hero_section",
    content: recommendations.primary || {},
    action: action
  }, {
    element: "recommendations_rail",
    content: recommendations.secondary || [],
    action: "personalized_products"
  }, {
    element: "exit_popup",
    content: recommendations.exit_intent || {},
    action: "exit_trigger"
  }],
  metadata: {
    intent: intent,
    confidence: vars.intent_confidence,
    strategy: vars.personalization_strategy,
    applied_at: new Date().toISOString()
  }
};
return {
  experience_payload_json: JSON.stringify(experience),
  adaptations_count: experience.adaptations.length,
  applied_action: action,
  experience_session: experience.session_id,
  adaptation_applied: "yes"
};`,
          outputVariables: [
            { name: "experience_payload_json", type: "string" },
            { name: "adaptations_count", type: "number" },
            { name: "applied_action", type: "string" },
            { name: "experience_session", type: "string" },
            { name: "adaptation_applied", type: "string" },
          ],
        },
      }),

      // 7. Cross-channel consistency manager (runCode)
      n(
        "7",
        "runCode",
        "Ensure cross-channel personalization consistency",
        1480,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Sync personalization state across channels
const userId = vars.hubspot_contact_id || "anonymous";
const intent = vars.predicted_intent || "browse";
const segment = vars.value_segment || "new";
const session = vars.experience_session || "";
const channelState = {
  user_id: userId,
  active_intent: intent,
  segment: segment,
  current_session: session,
  channels: {
    web: { adapted: true, action: vars.applied_action },
    email: { next_send_personalized: true, category: vars.content_category },
    push: { suppress_if_web_active: true },
    sms: { suppress_during_session: true }
  },
  synced_at: new Date().toISOString()
};
return {
  channel_state_json: JSON.stringify(channelState),
  channels_synced: 4,
  web_adapted: "yes",
  email_personalization_queued: "yes",
  push_suppressed: "yes",
  sync_timestamp: channelState.synced_at
};`,
            outputVariables: [
              { name: "channel_state_json", type: "string" },
              { name: "channels_synced", type: "number" },
              { name: "web_adapted", type: "string" },
              { name: "email_personalization_queued", type: "string" },
              { name: "push_suppressed", type: "string" },
              { name: "sync_timestamp", type: "string" },
            ],
          },
        },
      ),

      // 8. Conversion event tracker (runCode)
      n("8", "runCode", "Track conversion events from personalization", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Track whether personalization led to a conversion
const event = vars.current_event || "";
const cartValue = parseFloat(vars.cart_value) || 0;
const intent = vars.predicted_intent || "browse";
const confidence = parseInt(vars.intent_confidence) || 0;
const isConversion = event === "purchase" || event === "signup" || event === "add_to_cart";
const conversionValue = isConversion ? cartValue : 0;
const attributionScore = isConversion && confidence > 50 ? "personalization_assisted" : isConversion ? "organic" : "no_conversion";
return {
  conversion_detected: isConversion ? "yes" : "no",
  conversion_value: conversionValue,
  conversion_type: isConversion ? event : "none",
  attribution: attributionScore,
  predicted_vs_actual: isConversion && intent.includes("purchas") ? "correct" : isConversion ? "partial" : "no_conversion",
  personalization_effective: isConversion && confidence > 50 ? "yes" : "no",
  tracking_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "conversion_detected", type: "string" },
            { name: "conversion_value", type: "number" },
            { name: "conversion_type", type: "string" },
            { name: "attribution", type: "string" },
            { name: "predicted_vs_actual", type: "string" },
            { name: "personalization_effective", type: "string" },
            { name: "tracking_timestamp", type: "string" },
          ],
        },
      }),

      // 9. Personalization performance monitor (Sheets)
      sheetsNode(
        "9",
        "Log personalization performance to Sheets",
        1960,
        "PersonalizationEngine",
        '[["{tracking_timestamp}","{user_name}","{value_segment}","{browsing_intent}","{predicted_intent}","{intent_confidence}","{recommended_action}","{adaptations_count}","{conversion_detected}","{conversion_value}","{attribution}","{personalization_effective}"]]',
      ),

      // 10. Anomaly + fallback handler (Slack)
      slackNode(
        "10",
        "Alert on personalization anomalies via Slack",
        2200,
        "⚠️ *Personalization Engine Alert*\\nUser: {user_name} ({user_email})\\nAnomaly detected: {is_anomalous}\\nSession: {experience_session} | Duration: {session_duration_seconds}s\\nIntent: {predicted_intent} (confidence: {intent_confidence}%)\\nConversion: {conversion_detected} | Value: ${conversion_value}\\nAttribution: {attribution}\\nAction: Review if anomalous behavior indicates bot traffic or system issue",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },
};
